import { StrictMode, useEffect, useState, type FormEvent } from "react";
import { createRoot } from "react-dom/client";
import useSWR from "swr";
import {
  Link,
  NavLink,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  createBrowserRouter,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router";
import logoUrl from "./logo.webp";
import heroUrl from "./hero.webp";
import heroHomeUrl from "./hero-home.webp";
import heroDiyUrl from "./hero-diy.webp";
import openingSoonUrl from "./opening-soon.webp";
import footerHeroUrl from "./footer-hero.webp";

import checkUrl from "./check.webp";

import iconSearchUrl from "./icons/search.webp";
import iconBasketUrl from "./icons/basket.webp";
import noImageLightUrl from "./no-image-light.webp";
import noImageDarkUrl from "./no-image-dark.webp";
import { legalPages } from "./legal-pages.generated";

const containerClass = "max-w-6xl mx-auto w-[95%]";
const grid12Class = "grid grid-cols-12 gap-[var(--gap)]";
const sectionClass = "pt-[2.25rem] pb-[1.7rem]";
const policyHighlightGridClass = "mb-[clamp(2rem,4vw,3rem)] grid grid-cols-2 gap-3 sm:grid-cols-4";
const policyHighlightCardClass = "min-h-[9.25rem] rounded-[0.3rem] border border-white/[0.13] border-t-[0.18rem] border-t-[#d6d6d6] bg-white/[0.03] bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.025))] p-[clamp(0.85rem,1.8vw,1.1rem)]";
const policyHighlightTitleClass = "m-0 text-[clamp(0.78rem,1.5vw,0.92rem)] font-black leading-[1.18] tracking-[0.04em] text-[#f7f8f8] uppercase";
const policyHighlightTextClass = "mt-[0.65rem] mb-0 text-[clamp(0.78rem,1.45vw,0.92rem)] leading-[1.45] text-[#b8c0c8]";
const policySummaryClass = "max-w-[68rem]";
const policySummaryHeaderClass = "max-w-[52rem]";
const policySummaryTitleClass = "m-0 text-balance text-[clamp(2.15rem,4.8vw,4.1rem)] leading-[0.98] text-[#f7f8f8] uppercase";
const policySummaryIntroClass = "mt-4 mb-0 text-base leading-[1.65] text-[#b8c0c8]";
const policySummaryGridClass = "mt-[clamp(1.6rem,3vw,2.4rem)] grid grid-cols-1 gap-[0.85rem] md:grid-cols-2";
const policySummarySectionClass = "rounded-[0.35rem] border border-white/[0.13] border-l-[0.18rem] border-l-[#dadada] bg-white/[0.035] p-[clamp(1rem,2vw,1.25rem)]";
const policySummarySectionTitleClass = "m-0 text-base leading-[1.2] text-[#f7f8f8] uppercase";
const policySummaryListClass = "mt-[0.85rem] mb-0 pl-[1.1rem] leading-[1.55] text-[#c8d0d8]";
const policySummaryListItemClass = "mt-[0.45rem] first:mt-0";
const policySummaryLinkClass = "mt-[clamp(1.6rem,3vw,2.3rem)] inline-flex min-h-[2.8rem] items-center justify-center rounded-[0.2rem] bg-[#e02128] px-[1.1rem] text-[0.78rem] font-black tracking-[0.04em] text-white uppercase";

type NavItem = {
  label: string;
  path: string;
};

type Feature = {
  title: string;
  text: string;
  image: string;
};

type Category = {
  title: string;
  subtitle: string;
  kind: "speaker" | "subwoofer" | "amplifier" | "accessory" | "dsp" | "damping";
  image: string;
};

type Product = {
  id: number;
  name: string;
  description: string;
  price: string;
  sale_price: string | null;
  filter: ProductFilter;
  brand: BrandSlug;
  kind: "set" | "coaxial" | "midrange" | "tweeter" | "amplifier" | "subwoofer" | "accessory" | "dsp";
};

type ApiProduct = {
  id: number;
  slug: string;
  name: string;
  description: string;
  priceCents: number;
  salePriceCents: number | null;
  brandSlug: string;
  kind: string;
  sku: string;
  imageUrl: string;
  inventoryStatus: string;
  stockQuantity: number | null;
  vatRate: number;
  categories: string[];
};

type ProductListResult = {
  products: Product[];
  total: number;
};

type Service = {
  icon: string;
  title: string;
  text: string;
};

type Brand = {
  name: string;
  slug: BrandSlug;
  image: string;
  url: string;
};

type ProductFilter = "speakers" | "amplifiers" | "subwoofers" | "dsp" | "accessories";
type BrandSlug = "steg" | "audiosystem" | "gt-trading" | "jl-audio" | "awave";

type ProductListRoute = {
  path: string;
  filter: ProductFilter | "sale";
  title: string;
  intro: string;
};

type BasketItem = {
  productId: number;
  quantity: number;
};

type DeliveryMethod = "pickup" | "parcel_locker" | "courier";

type CheckoutDraft = {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  deliveryMethod: DeliveryMethod;
  deliveryName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  discountCode: string;
};

type BasketCalculationItem = {
  productId: number;
  name: string;
  description: string;
  unitPriceCents: number;
  quantity: number;
  subtotalCents: number;
};

type BasketCalculationResponse = {
  ok: boolean;
  code?: string;
  error?: string;
  message?: string;
  currency?: string;
  deliveryCents?: number;
  deliveryMethod?: DeliveryMethod;
  discountCents?: number;
  discountCode?: string;
  items?: BasketCalculationItem[];
  subtotalCents?: number;
  totalCents?: number;
  totals?: {
    delivery: string;
    discount: string;
    subtotal: string;
    total: string;
  };
};

type PublicOrder = {
  id: string;
  status: string;
  paymentStatus: string;
  refundStatus: string;
  canPay: boolean;
  mollieCheckoutUrl: string | null;
  createdAt: string;
  paidAt: string | null;
  dispatchedAt: string | null;
  trackingCode: string;
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  address: {
    deliveryName: string;
    line1: string;
    line2: string;
    city: string;
    postalCode: string;
    country: string;
  };
  deliveryMethod: DeliveryMethod;
  discountCode: string | null;
  items: BasketCalculationItem[];
  totals: {
    subtotal: string;
    subtotalCents: number;
    discount: string;
    discountCents: number;
    delivery: string;
    deliveryCents: number;
    total: string;
    totalCents: number;
  };
};

type AdminOrderItem = {
  product_id: number;
  product_name: string;
  product_description: string;
  unit_price_cents: number;
  quantity: number;
  subtotal_cents: number;
};

type AdminOrder = Omit<PublicOrder, "items"> & {
  raw: Record<string, unknown>;
  items: AdminOrderItem[];
  statusEvents: Record<string, unknown>[];
  emailSends: Record<string, unknown>[];
  paymentEvents: Record<string, unknown>[];
  discountRedemptions: Record<string, unknown>[];
};

type AdminOrderStatus =
  | "assembling"
  | "delivered"
  | "completed"
  | "dispatched"
  | "return_initiated"
  | "full_refund_return_confirmed"
  | "full_refund_order_cancelled"
  | "partial_refund_return_confirmed"
  | "partial_refund_order_cancelled"
  | "cancelled_unpaid";

type PolicyHighlight = {
  title: string;
  text: string;
};

type PolicySummarySection = {
  title: string;
  bullets: string[];
};

type AppOutletContext = {
  basketItems: BasketItem[];
  addToBasket: (productId: number) => void;
  clearBasket: () => void;
  removeFromBasket: (productId: number) => void;
  updateBasketQuantity: (productId: number, quantity: number) => void;
};

const navItems: NavItem[] = [
  // { label: "Akcijas Prece", path: "/category/sale" },
  { label: "Skaļruņi", path: "/category/speakers" },
  { label: "Pastiprinātāji", path: "/category/amplifiers" },
  { label: "Subvuferi", path: "/category/subwoofers" },
  { label: "Audio procesori", path: "/category/dsp" },
  { label: "Aksesuāri", path: "/category/accessories" },
];

import featureBadgeUrl from "./features/badge.webp";
import featureLatviaUrl from "./features/latvia.webp";
import featureShieldUrl from "./features/shield.webp";
import featureWaveUrl from "./features/wave.webp";

const features: Feature[] = [
  {
    title: "Drošs pirkums",
    text: "14 dienu atgriešanas iespēja, garantija un pēc pirkuma atbalsts",
    image: featureBadgeUrl,
  },
  {
    title: "Piegāde Latvijā",
    text: "Atrodamies Rīgā un piegādājam preces visā Latvijā",
    image: featureLatviaUrl,
  },
  {
    title: "Klientu atbalsts",
    text: "Pārdošanas pārstāvji ir pieejami pa tālruni un e-pastu",
    image: featureShieldUrl,
  },
  {
    title: "Labākā kvalitāte",
    text: "Iegādājieties produktus no labākās kvalitātes ražotājiem",
    image: featureWaveUrl,
  },
];

import categorySpeakersUrl from "./categories/speakers.webp"
import categorySubwoofersUrl from "./categories/subwoofers.webp"
import categoryAmplifiersUrl from "./categories/amplifiers.webp"
import categoryAccessoriesUrl from "./categories/accessories.webp"
import categoryDampingUrl from "./categories/damping.webp"
import categoryDspUrl from "./categories/dsp.webp"

import brandAudioSystemGermanyUrl from "./brands/audio-system-germany.webp"
import brandAwaveUrl from "./brands/awave.webp"
import brandJlAudioUrl from "./brands/jl-audio.webp"
import brandStegUrl from "./brands/steg.webp"

const categories: Category[] = [
  {
    title: "Skaļruņi",
    subtitle: "Komponentu, koaksiālie un citi",
    kind: "speaker",
    image: categorySpeakersUrl },

  {
    title: "Pastiprinātāji",
    subtitle: "Jauda un precizitāte",
    kind: "amplifier",
    image: categoryAmplifiersUrl },
    {
      title: "Zemfrekvences skaļruņi",
      subtitle: "Dziļa, jaudīga un iespaidīga skaņa",
      kind: "subwoofer",
      image: categorySubwoofersUrl },
    {
    title: "Aksesuāri",
    subtitle: "Uzstādīšanai, kabeļiem un aprīkojumam",
    kind: "accessory",
    image: categoryAccessoriesUrl },
  {
    title: "Audio procesori (DSP)",
    subtitle: "Precīza katras detaļas regulēšana",
    kind: "dsp",
    image: categoryDspUrl },
  {
    title: "Skaņas un vibroizolācija",
    subtitle: "Mazāk trokšņu, labāka skaņa",
    kind: "damping",
    image: categoryDampingUrl },
];

const products: Product[] = [
  {
    id: 1,
    name: "Pulse PRC6.2",
    description: "6.5 collu komponentu skaļruņu sistēma",
    price: "199,99 €",
    sale_price: "169,99 €",
    filter: "speakers",
    brand: "steg",
    kind: "set",
  },
  {
    id: 2,
    name: "Pulse PCX6.2",
    description: "6.5 collu koaksiālais skaļrunis",
    price: "129,99 €",
    sale_price: null,
    filter: "speakers",
    brand: "audiosystem",
    kind: "coaxial",
  },
  {
    id: 3,
    name: "Pulse PM65",
    description: "6.5 collu vidējo frekvenču skaļrunis",
    price: "99,99 €",
    sale_price: null,
    filter: "speakers",
    brand: "gt-trading",
    kind: "midrange",
  },
  {
    id: 4,
    name: "Pulse TW25",
    description: "1 collas zīda kupola augstfrekvences skaļrunis",
    price: "49,99 €",
    sale_price: null,
    filter: "speakers",
    brand: "jl-audio",
    kind: "tweeter",
  },
  {
    id: 5,
    name: "Pulse AMP900",
    description: "900 W D klases monobloka pastiprinātājs",
    price: "249,99 €",
    sale_price: "219,99 €",
    filter: "amplifiers",
    brand: "awave",
    kind: "amplifier",
  },
  {
    id: 6,
    name: "Pulse AMP450",
    description: "Kompakts 4 kanālu pastiprinātājs",
    price: "189,99 €",
    sale_price: null,
    filter: "amplifiers",
    brand: "steg",
    kind: "amplifier",
  },
  {
    id: 7,
    name: "Pulse SUB12",
    description: "12 collu augstas amplitūdas zemfrekvences skaļrunis",
    price: "179,99 €",
    sale_price: null,
    filter: "subwoofers",
    brand: "audiosystem",
    kind: "subwoofer",
  },
  {
    id: 8,
    name: "Pulse SUB10",
    description: "10 collu zemfrekvences skaļrunis slēgtā korpusā",
    price: "149,99 €",
    sale_price: null,
    filter: "subwoofers",
    brand: "gt-trading",
    kind: "subwoofer",
  },
  {
    id: 9,
    name: "Pulse KIT4",
    description: "4 AWG pastiprinātāja vadu komplekts",
    price: "39,99 €",
    sale_price: null,
    filter: "accessories",
    brand: "jl-audio",
    kind: "accessory",
  },
  {
    id: 10,
    name: "Pulse MAT12",
    description: "Skaņas izolācijas lokšņu komplekts",
    price: "59,99 €",
    sale_price: null,
    filter: "accessories",
    brand: "awave",
    kind: "accessory",
  },
  {
    id: 11,
    name: "Pulse DSP8",
    description: "8 kanālu digitālais skaņas procesors",
    price: "299,99 €",
    sale_price: null,
    filter: "dsp",
    brand: "steg",
    kind: "dsp",
  },
];

const featuredProducts = products.filter((product) => product.sale_price !== null);

const productListRoutes: ProductListRoute[] = [
  {
    path: "/category/sale",
    filter: "sale",
    title: "Akcijas Preces",
    intro: "Filtrējiet produktus ar īpašu akcijas cenu.",
  },
  {
    path: "/category/speakers",
    filter: "speakers",
    title: "Skaļruņi",
    intro: "Filtrējiet komponentu sistēmas, koaksiālos, vidējo frekvenču un augstfrekvences skaļruņus.",
  },
  {
    path: "/category/amplifiers",
    filter: "amplifiers",
    title: "Pastiprinātāji",
    intro: "Filtrējiet kompaktus, daudzkanālu un lielas jaudas pastiprinātājus.",
  },
  {
    path: "/category/subwoofers",
    filter: "subwoofers",
    title: "Zemfrekvences skaļruņi",
    intro: "Filtrējiet zemfrekvences skaļruņus un automašīnai gatavas basu sistēmas.",
  },
  {
    path: "/category/dsp",
    filter: "dsp",
    title: "Audio procesori (DSP)",
    intro: "Filtrējiet digitālos skaņas procesorus precīzai audio sistēmas regulēšanai.",
  },
  {
    path: "/category/accessories",
    filter: "accessories",
    title: "Aksesuāri",
    intro: "Filtrējiet uzstādīšanas komplektus, skaņas apstrādes materiālus un sistēmas piederumus.",
  },
];

const brands: Brand[] = [
  { name: "STEG", slug: "steg", image: brandStegUrl, url: "https://www.stegaudio.de/" },
  { name: "Audiosystem", slug: "audiosystem", image: brandAudioSystemGermanyUrl, url: "https://www.audio-system.de/" },
  { name: "JL Audio", slug: "jl-audio", image: brandJlAudioUrl, url: "https://www.jlaudio.com/" },
  { name: "Awave", slug: "awave", image: brandAwaveUrl, url: "https://www.awave.audio/" },
];

const salesPolicyHighlights: PolicyHighlight[] = [
  {
    title: "14 dienu atteikums",
    text: "Patērētāji var atteikties no distances līguma 14 dienu laikā, ja precei nav piemērojams likumā noteikts izņēmums.",
  },
  {
    title: "Atgriešana pēc paziņojuma",
    text: "Pēc atteikuma paziņojuma prece jānosūta vai jānodod atpakaļ ne vēlāk kā 14 dienu laikā.",
  },
  {
    title: "Atmaksa līdz 14 dienām",
    text: "Atmaksa tiek veikta bez nepamatotas kavēšanās un ne vēlāk kā 14 dienu laikā pēc atteikuma paziņojuma saņemšanas.",
  },
  {
    title: "Piegāde Latvijā",
    text: "Pasūtījumi tiek piegādāti Latvijas teritorijā ar pakomātu vai kurjeru, atbilstoši preces izmēram un veidam.",
  },
  {
    title: "Bojāts sūtījums",
    text: "Ja iepakojums vai prece saņemšanas brīdī ir bojāta, nofotografējiet bojājumus un sazinieties ar mums.",
  },
  {
    title: "Garantijas tiesības",
    text: "Patērētāji var pieteikt prasījumu par līguma noteikumiem neatbilstošu preci divu gadu laikā no piegādes.",
  },
  {
    title: "Droši maksājumi",
    text: "Maksājumi tiek apstrādāti ar Mollie, un SC Audio neglabā pilnus maksājumu kartes datus.",
  },
  {
    title: "Palīdzība un sūdzības",
    text: "Par pasūtījumiem, atgriešanu, garantiju vai piegādi var rakstīt uz info@scaudio.lv.",
  },
];

const privacyPolicyHighlights: PolicyHighlight[] = [
  {
    title: "Tikai nepieciešamie dati",
    text: "Mēs apstrādājam tikai tos personas datus, kas nepieciešami vietnes darbībai, pasūtījumiem, piegādei un saziņai.",
  },
  {
    title: "Bez reklāmas izsekošanas",
    text: "SC Audio neizmanto trešo pušu analītikas, reklāmas vai mārketinga izsekošanas rīkus.",
  },
  {
    title: "Maksājumu dati drošībā",
    text: "Maksājumus apstrādā Mollie, un mēs neglabājam pilnus maksājumu kartes datus.",
  },
  {
    title: "Jūsu datu tiesības",
    text: "Par piekļuvi, labošanu, dzēšanu vai citiem datu jautājumiem var rakstīt uz info@scaudio.lv.",
  },
];

const returnPolicySections: PolicySummarySection[] = [
  {
    title: "14 dienu atteikuma tiesības",
    bullets: [
      "Patērētājs var atteikties no distances līguma 14 dienu laikā, nenorādot iemeslu.",
      "Atteikuma tiesības nav piemērojamas gadījumos, kad to neļauj normatīvie akti vai konkrētās preces raksturs.",
    ],
  },
  {
    title: "Kā paziņot par atgriešanu",
    bullets: [
      "Pirms termiņa beigām rakstiet uz info@scaudio.lv un skaidri norādiet, ka vēlaties atteikties no pirkuma.",
      "Paziņojumu var iesniegt brīvā formā; atteikuma veidlapas izmantošana nav obligāta.",
    ],
  },
  {
    title: "Preces nosūtīšana atpakaļ",
    bullets: [
      "Pēc atteikuma paziņojuma prece jānosūta vai jānodod atpakaļ ne vēlāk kā 14 dienu laikā.",
      "Termiņš ir ievērots, ja prece nosūtīta pirms 14 dienu termiņa beigām.",
    ],
  },
  {
    title: "Iepakojums un komplektācija",
    bullets: [
      "Prece jāiepako tā, lai pārvadāšanas laikā tā netiktu bojāta.",
      "Ieteicams pievienot visus piederumus, vadus, instrukcijas, dāvanas, dokumentus un iepakojuma sastāvdaļas.",
    ],
  },
  {
    title: "Atgriešanas izmaksas",
    bullets: [
      "Patērētājs sedz tiešās preces atgriešanas izmaksas.",
      "Izņēmums ir gadījumi, kad prece tiek atgriezta pārdevēja kļūdas, nepareizi piegādātas preces, neatbilstības vai drošuma atsaukuma dēļ.",
    ],
  },
  {
    title: "Atmaksa",
    bullets: [
      "Atmaksa tiek veikta bez nepamatotas kavēšanās un ne vēlāk kā 14 dienu laikā pēc atteikuma paziņojuma saņemšanas.",
      "Atmaksu var aizturēt līdz brīdim, kad prece ir saņemta atpakaļ vai ir iesniegts apliecinājums par tās nosūtīšanu.",
    ],
  },
  {
    title: "Izņēmumi",
    bullets: [
      "Atteikuma tiesības var nebūt piemērojamas personalizētām precēm, higiēniski jutīgām atvērtām precēm, atvērtam audio, video vai programmatūras iepakojumam.",
      "Ja konkrētai precei ir īpašs atteikuma ierobežojums, par to cenšamies informēt preces aprakstā vai pasūtījuma noformēšanas laikā.",
    ],
  },
];

const warrantyPolicySections: PolicySummarySection[] = [
  {
    title: "2 gadu prasījuma tiesības",
    bullets: [
      "Patērētājs var pieteikt prasījumu par līguma noteikumiem neatbilstošu preci divu gadu laikā no piegādes dienas.",
      "Šīs tiesības darbojas neatkarīgi no tā, vai precei ir arī ražotāja vai pārdevēja komercgarantija.",
    ],
  },
  {
    title: "Pieteikšanas termiņš",
    bullets: [
      "Prasījums jāiesniedz vismaz divu mēnešu laikā no dienas, kad neatbilstība ir atklāta.",
      "Ātra saziņa palīdz ātrāk izvērtēt problēmu un atrast piemērotāko risinājumu.",
    ],
  },
  {
    title: "Iespējamie risinājumi",
    bullets: [
      "Sākotnēji var prasīt, lai neatbilstība tiek novērsta vai prece tiek apmainīta pret atbilstošu preci.",
      "Normatīvajos aktos paredzētos gadījumos var prasīt cenas samazinājumu vai līguma atcelšanu un naudas atmaksu.",
    ],
  },
  {
    title: "Komercgarantija",
    bullets: [
      "Dažām precēm var būt ražotāja, izplatītāja vai pārdevēja komercgarantija.",
      "Komercgarantija neierobežo patērētāja likumīgās tiesības par līguma noteikumiem neatbilstošu preci.",
    ],
  },
  {
    title: "Kā pieteikt garantiju",
    bullets: [
      "Rakstiet uz info@scaudio.lv un pievienojiet pasūtījuma numuru vai citu pirkumu apliecinošu dokumentu.",
      "Norādiet preces nosaukumu, modeli, ja pieejams sērijas numuru, bojājuma aprakstu, foto vai video un vēlamo saziņas veidu.",
    ],
  },
  {
    title: "Pārbaude un serviss",
    bullets: [
      "Preci var būt nepieciešams nogādāt pārbaudei, diagnostikai vai autorizētam servisam.",
      "Ja neatbilstība apstiprinās, ar izskatīšanu, remontu, apmaiņu vai atgriešanu saistītās pamatotās izmaksas sedz pārdevējs atbilstoši likumam.",
    ],
  },
  {
    title: "Kad garantija neattiecas",
    bullets: [
      "Garantija neattiecas uz bojājumiem, kas radušies nepareizas lietošanas, triecienu, šķidruma, nepiemērotu kabeļu vai barošanas, neatļauta remonta vai modifikāciju dēļ.",
      "Izņēmumos ietilpst arī noņemtas plombas vai sērijas numuri, parasts nolietojums un bojājumi, kas radušies neatbilstošas atpakaļsūtīšanas iepakošanas dēļ.",
    ],
  },
  {
    title: "Nepareizi piegādāta prece",
    bullets: [
      "Ja saņemta nepareiza prece, modelis, daudzums vai komplektācija, sazinieties ar mums iespējami drīz.",
      "Rakstot uz info@scaudio.lv, pievienojiet pasūtījuma numuru, problēmas aprakstu un fotogrāfijas.",
    ],
  },
];

const apiBaseUrl = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
  ? "http://localhost:8787"
  : "https://api.scaudio.lv";
const basketStorageKey = "basket";
const checkoutDraftStorageKey = "checkout-draft";
const adminTokenStorageKey = "admin-api-token";
const adminOrderStatusLabels: Record<AdminOrderStatus, string> = {
  assembling: "Sagatavo nosūtīšanai",
  delivered: "Piegādāts",
  completed: "Pabeigts",
  dispatched: "Nosūtīts",
  return_initiated: "Atgriešana sākta",
  full_refund_return_confirmed: "Pilna atmaksa: atgriešana",
  full_refund_order_cancelled: "Pilna atmaksa: atcelts",
  partial_refund_return_confirmed: "Daļēja atmaksa: atgriešana",
  partial_refund_order_cancelled: "Daļēja atmaksa: atcelts",
  cancelled_unpaid: "Atcelts: nav apmaksāts",
};
const adminOrderStatuses = Object.keys(adminOrderStatusLabels) as AdminOrderStatus[];
const defaultCheckoutDraft: CheckoutDraft = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  country: "LV",
  customerEmail: "",
  customerName: "",
  customerPhone: "",
  deliveryMethod: "parcel_locker",
  deliveryName: "",
  discountCode: "",
  postalCode: "",
};

function getProductFilterFromCategories(categories: string[]): ProductFilter {
  const productFilter = productListRoutes.find((route): route is ProductListRoute & { filter: ProductFilter } =>
    route.filter !== "sale" && categories.includes(route.filter)
  );

  return productFilter?.filter ?? "accessories";
}

function toProduct(product: ApiProduct): Product {
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    price: formatCents(product.priceCents),
    sale_price: product.salePriceCents === null ? null : formatCents(product.salePriceCents),
    filter: getProductFilterFromCategories(product.categories),
    brand: product.brandSlug as BrandSlug,
    kind: product.kind as Product["kind"],
  };
}

async function fetchProductList(url: string): Promise<ProductListResult> {
  const response = await fetch(url);
  const result = await response.json().catch(() => null) as { products?: ApiProduct[]; message?: string; error?: string } | null;

  if (!response.ok || !result?.products) {
    throw new Error(result?.message || result?.error || "Produktus neizdevās ielādēt.");
  }

  return {
    products: result.products.map(toProduct),
    total: Number(response.headers.get("X-Total-Count") ?? result.products.length),
  };
}

function getProductsUrl(params: Record<string, string | number | boolean | undefined>) {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      searchParams.set(key, String(value));
    }
  }

  const query = searchParams.toString();
  return `${apiBaseUrl}/v1/products${query ? `?${query}` : ""}`;
}

function useProducts(params: Record<string, string | number | boolean | undefined>) {
  const url = getProductsUrl(params);
  const { data, error, isLoading } = useSWR<ProductListResult>(url, fetchProductList);

  return {
    error,
    isLoading,
    products: data?.products ?? [],
    total: data?.total ?? 0,
  };
}

function getProductById(productId: number) {
  return products.find((product) => product.id === productId);
}

function parseProductPrice(price: string) {
  return Number(price.replace(/[^\d,.-]/g, "").replace(",", "."));
}

function getActiveProductPrice(product: Product) {
  return product.sale_price ?? product.price;
}

function formatPrice(value: number) {
  return new Intl.NumberFormat("lv-LV", {
    currency: "EUR",
    style: "currency",
  }).format(value);
}

function formatAdminDate(value: string | null | undefined) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("lv-LV", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getJsonPreview(value: unknown) {
  return JSON.stringify(value, null, 2);
}

function sanitizeBasketItems(value: unknown): BasketItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const quantitiesByProductId = new Map<number, number>();

  for (const item of value) {
    if (!item || typeof item !== "object") {
      continue;
    }

    const productId = Number((item as Partial<BasketItem>).productId);
    const quantity = Math.floor(Number((item as Partial<BasketItem>).quantity));

    if (!Number.isInteger(productId) || !getProductById(productId) || quantity < 1) {
      continue;
    }

    quantitiesByProductId.set(productId, (quantitiesByProductId.get(productId) ?? 0) + quantity);
  }

  return [...quantitiesByProductId.entries()].map(([productId, quantity]) => ({ productId, quantity }));
}

function readStoredBasket(): BasketItem[] {
  try {
    const storedBasket = localStorage.getItem(basketStorageKey);

    if (!storedBasket) {
      return [];
    }

    return sanitizeBasketItems(JSON.parse(storedBasket));
  } catch {
    return [];
  }
}

function writeStoredBasket(items: BasketItem[]) {
  try {
    if (items.length === 0) {
      localStorage.removeItem(basketStorageKey);
      return;
    }

    localStorage.setItem(basketStorageKey, JSON.stringify(items));
  } catch {
    // The basket still works for the current session if persistent storage is unavailable.
  }
}

function sanitizeCheckoutDraft(value: unknown): CheckoutDraft {
  if (!value || typeof value !== "object") {
    return defaultCheckoutDraft;
  }

  const draft = value as Partial<CheckoutDraft>;
  const deliveryMethod: DeliveryMethod =
    draft.deliveryMethod === "pickup" || draft.deliveryMethod === "parcel_locker" || draft.deliveryMethod === "courier"
      ? draft.deliveryMethod
      : defaultCheckoutDraft.deliveryMethod;

  return {
    addressLine1: typeof draft.addressLine1 === "string" ? draft.addressLine1 : "",
    addressLine2: typeof draft.addressLine2 === "string" ? draft.addressLine2 : "",
    city: typeof draft.city === "string" ? draft.city : "",
    country: typeof draft.country === "string" ? draft.country : "LV",
    customerEmail: typeof draft.customerEmail === "string" ? draft.customerEmail : "",
    customerName: typeof draft.customerName === "string" ? draft.customerName : "",
    customerPhone: typeof draft.customerPhone === "string" ? draft.customerPhone : "",
    deliveryMethod,
    deliveryName: typeof draft.deliveryName === "string" ? draft.deliveryName : "",
    discountCode: typeof draft.discountCode === "string" ? draft.discountCode : "",
    postalCode: typeof draft.postalCode === "string" ? draft.postalCode : "",
  };
}

function readStoredCheckoutDraft(): CheckoutDraft {
  try {
    const storedDraft = localStorage.getItem(checkoutDraftStorageKey);

    if (!storedDraft) {
      return defaultCheckoutDraft;
    }

    return sanitizeCheckoutDraft(JSON.parse(storedDraft));
  } catch {
    return defaultCheckoutDraft;
  }
}

function writeStoredCheckoutDraft(draft: CheckoutDraft) {
  try {
    localStorage.setItem(checkoutDraftStorageKey, JSON.stringify(draft));
  } catch {
    // Checkout still works for the current session if persistent storage is unavailable.
  }
}

function getBasketItemQuantity(items: BasketItem[], productId: number) {
  return items.find((item) => item.productId === productId)?.quantity ?? 0;
}

function getCheckoutPayload(basketItems: BasketItem[], checkoutDraft: CheckoutDraft) {
  return {
    address: {
      city: checkoutDraft.city,
      country: checkoutDraft.country,
      deliveryName: checkoutDraft.deliveryName,
      line1: checkoutDraft.addressLine1,
      line2: checkoutDraft.addressLine2,
      postalCode: checkoutDraft.postalCode,
    },
    customer: {
      email: checkoutDraft.customerEmail,
      name: checkoutDraft.customerName,
      phone: checkoutDraft.customerPhone,
    },
    deliveryMethod: checkoutDraft.deliveryMethod,
    discountCode: checkoutDraft.discountCode,
    items: basketItems,
  };
}

function formatCents(cents: number) {
  return formatPrice(cents / 100);
}

const styles = `
:root {
  --bg: #080b0d;
  --panel: #111519;
  --panel-strong: #181d22;
  --text: #f7f8f8;
  --muted: #a6adb5;
  --soft: #747b84;
  --line: color-mix(in srgb, white 10%, transparent);
  --red: #e02128;
  --red-dark: #8d1217;
  --max-content: 72rem;
  --page-pad: clamp(1rem, 4vw, 3rem);
  --gap: clamp(0.75rem, 1.8vw, 1.25rem);
  color-scheme: dark;
  font-family: Arial, Helvetica, sans-serif;
  background: var(--bg);
  color: var(--text);
}

.setThemeLight {
  --bg: #f1f1f1;
  --text: #121212;
  --border:rgb(172, 170, 170);
  --noImage: url(${noImageLightUrl});
}
.setThemeDark {
  --bg: #010000;
  --text: #f7f8f8;
  --border: #1b1f22;
  --noImage: url(${noImageDarkUrl});
}

* {
  /* outline: 1px solid rgba(255, 255, 255, 0.2); */
}

html {
  min-width: 20rem;
  background: var(--bg);
}

body {
  margin: 0;
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
}

.legal-content {
  max-width: 68rem;
  color: #d8dde3;
  font-size: 1rem;
  line-height: 1.72;
}

.legal-content h1,
.legal-content h2,
.legal-content h3 {
  color: #f7f8f8;
  line-height: 1.08;
  text-wrap: balance;
}

.legal-content h1 {
  margin: 0 0 1.25rem;
  max-width: 52rem;
  font-size: clamp(2.15rem, 4.8vw, 4.1rem);
  text-transform: uppercase;
}

.legal-content h2 {
  margin: 2.5rem 0 0.85rem;
  font-size: clamp(1.35rem, 2.4vw, 2rem);
}

.legal-content h3 {
  margin: 2rem 0 0.75rem;
  font-size: 1.2rem;
}

.legal-content p,
.legal-content ul,
.legal-content ol,
.legal-content table {
  margin: 0 0 1.05rem;
}

.legal-content ul,
.legal-content ol {
  padding-left: 1.35rem;
}

.legal-content li + li {
  margin-top: 0.3rem;
}

.legal-content a {
  color: #f7f8f8;
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, #e02128 70%, transparent);
  text-underline-offset: 0.22rem;
}

.legal-content code {
  border-radius: 0.2rem;
  background: rgba(255, 255, 255, 0.08);
  padding: 0.08rem 0.28rem;
  color: #f7f8f8;
  font-size: 0.92em;
}

.legal-content table {
  display: block;
  width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.legal-content th,
.legal-content td {
  min-width: 12rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 0.75rem;
  text-align: left;
  vertical-align: top;
}

.legal-content th {
  background: rgba(224, 33, 40, 0.16);
  color: #f7f8f8;
}

.legal-content tr:nth-child(even) td {
  background: rgba(255, 255, 255, 0.03);
}

.legal-content hr {
  margin: 2rem 0;
  border: 0;
  border-top: 1px solid var(--line);
}

@media (max-width: 36rem) {
  :root {
    --page-pad: 1rem;
    --gap: 0.65rem;
  }
}


@keyframes child1-fade {
    0%, 45%   { opacity: 1; }
    50%, 95%  { opacity: 0; }
    100%      { opacity: 1; }
  }

  .child-1 {
    animation: child1-fade 10s linear infinite;
  }

  .child-2 {
    animation: child1-fade 10s linear infinite;
    animation-delay: -5s;
  }
`;

function Logo() {
  return (
    <Link className="inline-flex min-w-max items-center uppercase" to="/" aria-label="SC Audio sākumlapa">
      <img
        className="block h-auto w-[clamp(8.8rem,16vw,12rem)] object-contain max-[36rem]:w-[clamp(7rem,34vw,9rem)]"
        src={logoUrl}
        alt="SC Audio"
      />
    </Link>
  );
}

function HeaderBanner() {
  return (
    <div className="flex py-2 bg-[#b61c1d] relative">
      <div className={`${containerClass} text-balance text-center text-xs tracking-[0.03em] opacity-0`}>{"Bezmaksas piegāde pasūtījumiem virs 500 EUR"}</div>
      <div className={`${containerClass} text-balance text-center text-xs tracking-[0.03em] child-1 absolute inset-0 flex items-center justify-center`}>{"Piegāde ar Omniva, DPD un kurjerpastu"}</div>
      <div className={`${containerClass} text-balance text-center text-xs tracking-[0.03em] child-2 absolute inset-0 flex items-center justify-center`}>{"Bezmaksas piegāde pasūtījumiem virs 500 EUR"}</div>
    </div>
  );
}

function HeaderLinks() {
  const topLinkClass = ({ isActive }: { isActive: boolean }) => `${(isActive ? "text-red-500" : "text-[#a6adb5] hover:text-white")} text-center flex items-center`;
  return (
    <div className="py-2">
      <div className={`${containerClass} flex justify-between font-extrabold uppercase text-xs tracking-[0.03em]`}>
        <div className="pl-8 sm:block hidden text-[#a6adb5]">{"Hanzas iela 14, Rīga"}</div>
        <div className="flex gap-6 sm:flex-none flex-1 justify-around">
          <NavLink to="/" className={topLinkClass}>{"Sākums"}</NavLink>
          <NavLink to="/about" className={topLinkClass}>{"Par mums"}</NavLink>
          <NavLink to="/contact" className={topLinkClass}>{"Sazināties"}</NavLink>
          <a href="tel:+37126386666"><span className="hidden sm:inline">{"Zvanīt: +371 "}</span>{"26386666"}</a>
        </div>
      </div>
    </div>
  );
}

function HeaderMain({ basketQuantity }: { basketQuantity: number }) {
  const categoryLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative after:absolute after:inset-x-0 after:bottom-[-5px] after:h-[2px] after:content-[''] ${isActive
      ? "text-[#e02128] after:bg-[#e02128]"
      : "text-white after:bg-transparent hover:after:bg-white/30"
    }`;

  return (
    <div className="py-2 bg-[#050708] font-extrabold uppercase text-xs tracking-[0.03em]">
      <div className={`${containerClass} flex justify-between`}>
        <div className=""><Logo /></div>
        <div className="md:flex hidden flex gap-6 items-center">
          {navItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.path}
              className={categoryLinkClass}
              end={item.path === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
        <div
          className="flex min-w-max items-center justify-end gap-[0.95rem] max-[36rem]:gap-[0.35rem]"
          aria-label="Veikala darbības"
        >
          <button
            className="relative grid h-[1.65rem] w-[1.65rem] place-items-center rounded-full border border-transparent bg-transparent text-[#f7f8f8]"
            type="button"
            aria-label="Meklēt"
          >
            <img src={iconSearchUrl} alt="Meklēt" className="w-full max-w-4 max-h-4" />
          </button>
          <Link
            className="relative grid h-[1.65rem] w-[1.65rem] place-items-center rounded-full border border-transparent bg-transparent text-[#f7f8f8]"
            aria-label="Grozs"
            to="/basket"
          >
            <img src={iconBasketUrl} alt="Grozs" className="w-full max-w-4 max-h-4" />
            <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-[#e02128] text-[0.6rem] font-extrabold text-white">
              {basketQuantity}
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section
      className="border-b border-white/10 bg-[#010000] bg-cover bg-no-repeat"
      style={{
        backgroundImage: `linear-gradient(90deg, #010000 0 30%, transparent 50%), url(${heroUrl})`,
        backgroundPosition: "center, right center",
      }}
    >
      <div className={`${containerClass} items-center sm:py-16 py-4`}>
        <div className="col-span-4">
          {/* <p className="text-sm font-extrabold uppercase tracking-[0.04em] text-[#e02128] mb-2 text-shadow-lg text-shadow-black">{"Auto audio izplatītājs Latvijā"}</p> */}
          <h1 className="font-black uppercase leading-[0.95] tracking-normal text-6xl text-balance max-w-xl mb-2 text-shadow-lg text-shadow-black">{"Sadzirdi katru "}<span className="text-[#e02128]">{"skaņu."}</span></h1>
          <p className="max-w-lg leading-[1.65] text-white/80 text-balance mb-4 text-shadow-lg text-shadow-black">{"Audio tehnika gandrīz jebkurai gaumei"}</p>
          <div className="flex items-center gap-4 sm:flex-row flex-col">
            <Link
              className="min-h-10 flex items-center justify-center rounded bg-[#e02128] hover:bg-[#b51a1f] px-4 font-black uppercase tracking-[0.03em] text-white"
              to="/category"
            >{"Apskatīt kategorijas"}</Link>
            <Link
              className="min-h-10 flex items-center justify-center px-4 font-black uppercase tracking-[0.03em] text-white underline text-shadow-lg"
              to="/brand"
            >{"Apskatīt zīmolus"}</Link>
          </div>
        </div>
      </div>
      {/* <div className="absolute bottom-[1.4rem] right-1/2 flex translate-x-1/2 gap-[1.1rem]" aria-hidden="true">
        <span className="h-[0.12rem] w-8 bg-[#e02128]" />
        <span className="h-[0.12rem] w-8 bg-white/60" />
        <span className="h-[0.12rem] w-8 bg-white/60" />
      </div> */}
    </section>
  );
}
function HeroSubdivided() {
  return (
    <section className="border-b border-white/10 bg-[#010000] " >
      <div className={`${containerClass} flex sm:flex-row flex-col sm:hidden`}>
        <div className="flex-1 flex justify-center items-center bg-cover bg-no-repeat min-h-30 p-8"
          style={{
            backgroundImage: `url(${heroDiyUrl})`,
            backgroundPosition: "right center",
          }}
        >
          <div className="">
            <h1 className="text-[#e0e0e0] font-black uppercase leading-[0.95] tracking-normal text-5xl text-balance mb-2 text-shadow-lg text-shadow-black">{"DIY Audio"}</h1>
            <p className="max-w-lg leading-[1.65] text-white/80 text-balance text-shadow-lg text-shadow-black">{"Atklāšana drīz"}</p>
          </div>
        </div>
        <div className="flex-1 flex justify-center items-center bg-cover bg-no-repeat min-h-30 p-8"
          style={{
            backgroundImage: `url(${heroHomeUrl})`,
            backgroundPosition: "right center",
          }}
        >
          <div className="">
            <h1 className="text-[#fefef3] font-black uppercase leading-[0.95] tracking-normal text-5xl text-balance mb-2 text-shadow-lg text-shadow-black">{"Home Audio"}</h1>
            <p className="max-w-lg leading-[1.65] text-white/80 text-balance text-shadow-lg text-shadow-black">{"Atklāšana drīz"}</p>
          </div>
        </div>
      </div>
      <div className={`${containerClass} sm:flex-row flex-col hidden sm:flex`}>
        <div className="flex-1 flex justify-center items-center bg-cover bg-no-repeat min-h-28 p-8 relative group"
          style={{
            backgroundImage: `linear-gradient(to right, color-mix(in srgb, #010000 100%, transparent) 0%, transparent 50%), url(${heroDiyUrl})`,
            backgroundPosition: "center, right center",
          }}
        >
          <div className="absolute top-0 left-0 w-full h-full group-hover:opacity-40 opacity-0 bg-white transition-opacity mix-blend-overlay pointer-events-none"></div>
          <div className="">
            <h1 className="text-[#e0e0e0] font-black uppercase leading-[0.95] tracking-normal text-5xl text-balance mb-2 text-shadow-lg text-shadow-black">{"DIY Audio"}</h1>
            <p className="max-w-lg leading-[1.65] text-white/80 text-balance text-shadow-lg text-shadow-black">{"Atklāšana drīz"}</p>
          </div>
        </div>
        <div className="flex-1 flex justify-center items-center bg-cover bg-no-repeat min-h-28 p-8 relative group"
          style={{
            backgroundImage: `linear-gradient(to left, color-mix(in srgb, #010000 100%, transparent) 0%, transparent 50%), url(${heroHomeUrl})`,
            backgroundPosition: "center, right center",
          }}
        >
          <div className="absolute top-0 left-0 w-full h-full group-hover:opacity-40 opacity-0 bg-white transition-opacity mix-blend-overlay pointer-events-none"></div>
          <div className="">
            <h1 className="text-[#fefef3] font-black uppercase leading-[0.95] tracking-normal text-5xl text-balance mb-2 text-shadow-lg text-shadow-black">{"Home Audio"}</h1>
            <p className="max-w-lg leading-[1.65] text-white/80 text-balance text-shadow-lg text-shadow-black">{"Atklāšana drīz"}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section
      className="border-b border-[var(--line)] bg-[#0d1013] bg-[image:linear-gradient(90deg,transparent,color-mix(in_srgb,white_4%,transparent),transparent)]"
      aria-label="Veikala priekšrocības"
    >
      <div className={`${containerClass} grid grid-cols-12 gap-4 py-[1.6rem]`}>
        {features.map((feature) => (
          <div className="flex gap-2 lg:flex-row flex-col sm:col-span-3 col-span-6">
            <div className="flex-1 flex items-center justify-center">
              <img src={feature.image} alt={feature.title} className="h-12 w-12 object-contain" />
            </div>
            <div className="flex-2 flex flex-col justify-center gap-2">
              <h3 className="text-sm font-black uppercase tracking-[0.02em] text-center lg:text-left">{feature.title}</h3>
              <p className="text-xs leading-[1.45] text-[#a6adb5] text-balance text-center lg:text-left">{feature.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHead({
  title,
  action,
  to = "#",
  className = "",
}: {
  title: string;
  action: string;
  to?: string;
  className?: string;
}) {
  return (
    <div className={`${className} bg-[var(--bg)] text-[var(--text)]`}>
      <div className={`${containerClass} flex justify-between min-h-[2.1rem]`}>
        <h2 className="inline-flex items-center justify-center text-lg font-black uppercase tracking-[0.01em]">{title}</h2>
        <Link
          className="hidden sm:inline-flex items-center justify-center border border-[var(--border)] font-black uppercase tracking-[0.04em] px-4"
          to={to}
        >{action}</Link>
      </div>
    </div>
  );
}

function getCategoryPath(category: Category) {
  const routeByKind: Record<Category["kind"], string> = {
    speaker: "/category/speakers",
    subwoofer: "/category/subwoofers",
    amplifier: "/category/amplifiers",
    accessory: "/category/accessories",
    dsp: "/category/dsp",
    damping: "/category/accessories",
  };

  return routeByKind[category.kind];
}

function getBrandPath(brand: Brand) {
  return `/brand/${brand.slug}`;
}

function getProductPath(product: Product) {
  return `/product/${product.id}`;
}

function getProductCategoryRoute(product: Product) {
  return productListRoutes.find((route) => route.filter === product.filter) ?? productListRoutes[0]!;
}

function getProductImages(_product: Product) {
  return [noImageLightUrl];
}

function CategoryCard({ category }: { category: Category }) {
  return (
    <Link
      className={`md:col-span-3 sm:col-span-4 col-span-6 min-h-40 content-end p-4 bg-cover bg-no-repeat border border-1 border-white/10 rounded`}
      to={getCategoryPath(category)}
      style={{
        backgroundImage: `linear-gradient(to bottom, transparent 80%, color-mix(in srgb, #010000 80%, transparent)), url(${category.image})`,
        backgroundPosition: "center, top center",
      }}
    >
      <h3 className="relative z-[1] m-0 text-center text-[0.86rem] font-black uppercase after:mx-auto after:mt-2 after:block after:h-[0.12rem] after:w-[2.1rem] after:bg-[var(--red)] after:content-[''] max-[36rem]:text-[0.74rem]">
        {category.title}
      </h3>
      <h3 className="relative z-[1] m-0 mt-2 text-center text-[0.72rem] font-black uppercase text-[#a6adb5] max-[36rem]:text-[0.66rem]">
        {category.subtitle}
      </h3>
    </Link>
  );
}

function CategorySection() {
  return (
    <section className={`${sectionClass} ${containerClass}`}>
      <div className={grid12Class}>
        {categories.map((category) => (
          <CategoryCard key={category.title} category={category} />
        ))}
      </div>
    </section>
  );
}

function ProductCard({
  basketQuantity,
  product,
  className = "col-span-6 sm:col-span-3",
  onAddToBasket,
  onRemoveFromBasket,
}: {
  basketQuantity: number;
  product: Product;
  className?: string;
  onAddToBasket: (productId: number) => void;
  onRemoveFromBasket: (productId: number) => void;
}) {
  return (
    <article className={`${className} flex flex-col gap-1 h-74 rounded-lg border border-[var(--border)] bg-white shadow-lg overflow-hidden`}>
      <Link className="flex-1 overflow-hidden" to={getProductPath(product)}>
        <img className="w-full h-full object-contain" src={noImageLightUrl}/>
      </Link>
      <Link className="flex-none font-black uppercase px-2" to={getProductPath(product)}>{product.name}</Link>
      {/* <p className="text-sm leading-[1.35]">{product.description}</p> */}
      {/* <div className="flex items-center justify-between"> */}
      <ProductCardPrice product={product} />
      {/* {basketQuantity > 0 ? (
        <Link className="text-center text-sm font-black uppercase" to={getProductPath(product)}>
          {basketQuantity} grozā
        </Link>
      ) : null} */}
      <ProductBasketButtons basketQuantity={basketQuantity} product={product} onAddToBasket={onAddToBasket} onRemoveFromBasket={onRemoveFromBasket} />
    </article>
  );
}

function ProductCardPrice({
  product,
}: {
  product: Product;
}) {
  return (
  <Link className="flex-none px-2" to={getProductPath(product)}>
      {product.sale_price !== null ? (
        <div className="flex justify-between">
          <div >
            <span className="font-black text-red-600">{product.sale_price}</span>
            <span className="text-xs text-red-600">{" / gab."}</span>
          </div>
          <div>
            <span className="font-black line-through opacity-70">{product.price}</span>
            <span className="text-xs">{" / gab."}</span>
          </div>
        </div>
      ) : (
        <div>
          <span className="font-black text-red-600">{product.price}</span>
          <span className="text-xs text-red-600">{" / gab."}</span>
        </div>
      )}
    </Link>
  )
};
function ProductBasketButtons({
  basketQuantity,
  product,
  onAddToBasket,
  onRemoveFromBasket,
}: {
  basketQuantity: number;
  product: Product;
  onAddToBasket: (productId: number) => void;
  onRemoveFromBasket: (productId: number) => void;
}) {
  return <div className="flex-none flex sm:gap-2 gap-0 text-sm sm:flex-row flex-col">
    {basketQuantity > 0 ? (
      <>
        <button
          className="flex-[20px] min-h-9 uppercase bg-red-500 hover:bg-red-600 cursor-pointer text-white shadow px-2"
          type="button"
          aria-label={`Noņemt ${product.name} no groza`}
          onClick={() => onRemoveFromBasket(product.id)}
        >{"-1"}</button>
        <Link
          className="flex-3 inline-flex min-h-9 items-center justify-center border border-[var(--border)] px-2 text-center font-black uppercase text-[var(--text)]"
          to="/basket"
        >{`Uz grozu (${basketQuantity})`}</Link>
        <button
          className="flex-1 min-h-9 uppercase bg-red-500 hover:bg-red-600 cursor-pointer text-white shadow px-2"
          type="button"
          aria-label={`Pievienot vēl vienu ${product.name} grozam`}
          onClick={() => onAddToBasket(product.id)}
        >{"+1"}</button>
      </>
    ) : (
      <button
        className="flex-[20px] uppercase text-sm bg-red-500 hover:bg-red-600 cursor-pointer text-white shadow py-2"
        type="button"
        aria-label={`Pievienot ${product.name} grozam`}
        onClick={() => onAddToBasket(product.id)}
      >{"Ielikt grozā"}</button>
    )}
  </div>;
}

function ProductSection({
  basketItems,
  className = "",
  onAddToBasket,
  onRemoveFromBasket,
}: {
  basketItems: BasketItem[];
  className?: string,
  onAddToBasket: (productId: number) => void;
  onRemoveFromBasket: (productId: number) => void;
}) {
  const { error, isLoading, products: featuredProducts } = useProducts({ is_discount: true });

  return (
    <div className={`${className} bg-[var(--bg)] text-[var(--text)]`}>
      <section className={`${sectionClass} ${containerClass}`}>
        <div className={grid12Class}>
          {isLoading ? (
            <div className="col-span-full grid min-h-52 place-items-center rounded-[0.35rem] border border-[var(--border)] bg-white text-center text-[#747b84]">
              Ielādē produktus...
            </div>
          ) : error ? (
            <div className="col-span-full grid min-h-52 place-items-center rounded-[0.35rem] border border-[var(--border)] bg-white text-center text-[#747b84]">
              Produktus neizdevās ielādēt.
            </div>
          ) : featuredProducts.length > 0 ? (
            featuredProducts.map((product) => (
              <ProductCard
                basketQuantity={getBasketItemQuantity(basketItems, product.id)}
                key={product.id}
                product={product}
                className={`${className} col-span-4 sm:col-span-3`}
                onAddToBasket={onAddToBasket}
                onRemoveFromBasket={onRemoveFromBasket}
              />
            ))
          ) : (
            <div className="col-span-full grid min-h-52 place-items-center rounded-[0.35rem] border border-[var(--border)] bg-white text-center text-[#747b84]">
              Šobrīd nav akcijas preču.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ProductListPage({
  basketItems,
  route,
  onAddToBasket,
  onRemoveFromBasket,
}: {
  basketItems: BasketItem[];
  route: ProductListRoute;
  onAddToBasket: (productId: number) => void;
  onRemoveFromBasket: (productId: number) => void;
}) {
  const productParams = route.filter === "sale" ? { is_discount: true } : { category: route.filter };
  const { error, isLoading, products: visibleProducts } = useProducts(productParams);
  const { products: allProducts } = useProducts({ per_page: 1000 });

  return (
    <main className="setThemeLight bg-[var(--bg)] text-[var(--text)] py-12">
      <div className={`${containerClass} flex flex-col gap-4`}>
        <header className="">
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">{route.title}</h1>
        </header>
        <header className="">
          <p className="m-0 text-base leading-[1.6] text-[#747b84]">{route.intro}</p>
        </header>
        <section className="grid grid-cols-12 gap-4" aria-label={`${route.title} produkti`}>
          {isLoading ? (
            <div className="col-span-full grid min-h-64 place-items-center rounded border border-[var(--border)] bg-white">
              Ielādē produktus...
            </div>
          ) : error ? (
            <div className="col-span-full grid min-h-64 place-items-center rounded border border-[var(--border)] bg-white">
              Produktus neizdevās ielādēt.
            </div>
          ) : visibleProducts.length > 0 ? (
            visibleProducts.map((product) => (
              <ProductCard
                basketQuantity={getBasketItemQuantity(basketItems, product.id)}
                key={product.id}
                product={product}
                onAddToBasket={onAddToBasket}
                onRemoveFromBasket={onRemoveFromBasket}
              />
            ))
          ) : (
            <div className="col-span-full grid min-h-64 place-items-center rounded border border-[var(--border)] bg-white">
              Šim filtram neatbilst neviens produkts.
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function CategoryRootPage() {
  return (
    <main className="setThemeLight min-h-[42rem] border-t border-[var(--border)] bg-[var(--bg)] py-[clamp(2rem,5vw,4rem)] text-[var(--text)]">
      <div className={`${containerClass} mb-4 grid max-w-[42rem] gap-3`}>
        <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
          Kategorijas
        </p>
        <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">Produktu kategorijas</h1>
        <p className="m-0 text-base leading-[1.6] text-[#747b84]">Izvēlieties kategoriju, lai apskatītu pieejamos produktus.</p>
      </div>
      <CategorySection />
    </main>
  );
}

function BrandRootPage() {
  return (
    <main className="setThemeLight min-h-[42rem] border-t border-[var(--border)] bg-[var(--bg)] py-[clamp(2rem,5vw,4rem)] text-[var(--text)]">
      <div className={`${containerClass} grid gap-[var(--gap)]`}>
        <header className="mb-4 grid max-w-[42rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
            Zīmoli
          </p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">Pārstāvētie zīmoli</h1>
          <p className="m-0 text-base leading-[1.6] text-[#747b84]">Apskatiet produktus pēc zīmola.</p>
        </header>
        <section className="grid gap-3" aria-label="Pārstāvētie zīmoli">
          {brands.map((brand) => (
            <Link
              className="grid min-h-24 grid-cols-[8rem_minmax(0,1fr)] items-center gap-5 rounded-[0.35rem] border border-[var(--border)] bg-white p-5 text-[#121212] shadow-[0_0.4rem_1.4rem_rgba(0,0,0,0.06)] hover:border-[#e02128]/60"
              key={brand.slug}
              to={getBrandPath(brand)}
            >
              <span className="grid min-h-16 place-items-center">
                <img src={brand.image} alt={brand.name} className="max-h-12 max-w-32 object-contain" />
              </span>
              <span className="text-lg font-black uppercase">{brand.name}</span>
            </Link>
          ))}
        </section>
      </div>
    </main>
  );
}

function BrandListPage({
  basketItems,
  brand,
  onAddToBasket,
  onRemoveFromBasket,
}: {
  basketItems: BasketItem[];
  brand: Brand;
  onAddToBasket: (productId: number) => void;
  onRemoveFromBasket: (productId: number) => void;
}) {
  const { error, isLoading, products: visibleProducts } = useProducts({ brand: brand.slug });
  const { products: allProducts } = useProducts({ per_page: 1000 });

  return (
    <main className="setThemeLight min-h-[42rem] border-t border-[var(--border)] bg-[var(--bg)] py-[clamp(2rem,5vw,4rem)] text-[var(--text)]">
      <div className={`${containerClass} ${grid12Class}`}>
        <header className="col-span-full mb-4 grid max-w-[42rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
            Zīmola filtrs
          </p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">{brand.name}</h1>
          <p className="m-0 text-base leading-[1.6] text-[#747b84]">Filtrējiet produktus no {brand.name} sortimenta.</p>
        </header>

        <aside
          className="col-span-full grid grid-cols-2 gap-[0.65rem] self-start rounded-[0.35rem] border border-[var(--border)] bg-white p-3 text-[#121212] shadow-[0_0.4rem_1.4rem_rgba(0,0,0,0.06)] sm:col-span-3 sm:p-4"
          aria-label="Zīmolu filtri"
        >
          <h2 className="col-span-full m-0 mb-[0.35rem] text-[0.8rem] uppercase tracking-[0.04em]">Filtrēt pēc zīmola</h2>
          {brands.map((item) => {
            const count = allProducts.filter((product) => product.brand === item.slug).length;

            return (
              <NavLink
                className={({ isActive }) =>
                  `flex min-h-8 items-center justify-between gap-4 rounded border px-[0.55rem] text-[0.66rem] font-black uppercase tracking-[0.03em] sm:min-h-[2.35rem] sm:px-[0.8rem] sm:text-[0.78rem] ${
                    isActive
                      ? "border-[#e02128] bg-[#e02128] text-white"
                      : "border-black/10 text-[#747b84]"
                  }`
                }
                key={item.slug}
                to={getBrandPath(item)}
              >
                <span>{item.name}</span>
                <span>{count}</span>
              </NavLink>
            );
          })}
        </aside>

        <section className="col-span-full grid grid-cols-2 gap-[var(--gap)] sm:col-span-9 sm:grid-cols-4" aria-label={`${brand.name} produkti`}>
          {isLoading ? (
            <div className="col-span-full grid min-h-64 place-items-center rounded-[0.35rem] border border-[var(--border)] bg-white text-center text-[#747b84]">
              Ielādē produktus...
            </div>
          ) : error ? (
            <div className="col-span-full grid min-h-64 place-items-center rounded-[0.35rem] border border-[var(--border)] bg-white text-center text-[#747b84]">
              Produktus neizdevās ielādēt.
            </div>
          ) : visibleProducts.length > 0 ? (
            visibleProducts.map((product) => (
              <ProductCard
                basketQuantity={getBasketItemQuantity(basketItems, product.id)}
                className=""
                key={product.id}
                product={product}
                onAddToBasket={onAddToBasket}
                onRemoveFromBasket={onRemoveFromBasket}
              />
            ))
          ) : (
            <div className="col-span-full grid min-h-64 place-items-center rounded-[0.35rem] border border-[var(--border)] bg-white text-center text-[#747b84]">
              Šim zīmolam neatbilst neviens produkts.
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function BrandRail() {
  return (
    <>
    <section className="border-t border-b border-white/10 py-6" aria-label="Pārstāvētie zīmoli">
      <div className={`${containerClass} flex justify-around gap-16 flex-wrap`}>
        {brands.map((brand) => (
          <Link to={getBrandPath(brand)} key={brand.name}>
            <img src={brand.image} alt={brand.name} className="max-w-32 max-h-12" />
          </Link>
        ))}
      </div>
    </section>
    </>
  );
}

function ProductDetailPage({
  basketItems,
  onAddToBasket,
  onRemoveFromBasket,
}: {
  basketItems: BasketItem[];
  onAddToBasket: (productId: number) => void;
  onRemoveFromBasket: (productId: number) => void;
}) {
  const { productId } = useParams();
  const product = getProductById(Number(productId));

  if (!product) {
    return <NotFoundPage />;
  }

  const categoryRoute = getProductCategoryRoute(product);
  const productImages = getProductImages(product);
  const basketQuantity = getBasketItemQuantity(basketItems, product.id);
  const hasSalePrice = product.sale_price !== null;

  return (
    <main className="setThemeLight min-h-[42rem] border-t border-[var(--border)] bg-[var(--bg)] py-[clamp(2rem,5vw,4rem)] text-[var(--text)]">
      <div className={`mb-4`}>
        <nav className={`${containerClass} flex flex-wrap items-center gap-2 text-[0.78rem] font-black uppercase tracking-[0.04em] text-[#747b84]`} aria-label="Produkta atrašanās vieta">
          <Link className="hover:text-[#e02128]" to="/">Sākums</Link>
          <span aria-hidden="true">/</span>
          <Link className="hover:text-[#e02128]" to="/category">Kategorijas</Link>
          <span aria-hidden="true">/</span>
          <Link className="hover:text-[#e02128]" to={categoryRoute.path}>{categoryRoute.title}</Link>
          <span aria-hidden="true">/</span>
          <span className="text-[#121212]">{product.name}</span>
        </nav>
      </div>
      <div className={`${containerClass} grid grid-cols-12 gap-4 bg-white border border-[var(--border)] rounded`}>
        <section className="h-40 sm:h-auto col-span-full sm:col-span-6 flex flex-row sm:flex-col overflow-y-visible overflow-x-scroll sm:overflow-x-visible sm:overflow-y-visible" aria-label={`${product.name} attēli`}>
          {productImages.map((image, index) => (
            <img src={image} alt={`${product.name} attēls ${index + 1}`} className="flex-none" />
          ))}
          {productImages.map((image, index) => (
            <img src={image} alt={`${product.name} attēls ${index + 1}`} className="flex-none" />
          ))}
          {productImages.map((image, index) => (
            <img src={image} alt={`${product.name} attēls ${index + 1}`} className="flex-none" />
          ))}
        </section>
        <section className="col-span-full sm:col-span-6 flex flex-col gap-4 p-4" aria-label="Produkta informācija">
          <div className="">
            <p className="m-0 mb-3 text-[0.78rem] font-black uppercase tracking-[0.04em] text-[#e02128]">{categoryRoute.title}</p>
            <h1 className="m-0 text-[clamp(2rem,4vw,3.4rem)] font-black leading-[0.95] uppercase">{product.name}</h1>
            <p className="mt-4 mb-0 text-base leading-[1.6] text-[#747b84]">{product.description}</p>
            <p className="mt-3 mb-0 text-xs font-black uppercase tracking-[0.04em] text-[#747b84]">ID: {product.id}</p>
          </div>
          <div className="">
            {hasSalePrice ? (
              <div className="grid gap-1">
                <span className="text-base font-black text-[#747b84] line-through">{product.price}</span>
                <span className="text-3xl font-black text-[#e02128]">{product.sale_price}</span>
              </div>
            ) : (
              <span className="text-3xl font-black text-[#e02128]">{product.price}</span>
            )}
          </div>
          <div className="rounded border border-[var(--border)] bg-[#f7f8f8] p-4 text-sm font-bold">
            Prece atrodas Rīgas noliktavā — nosūtīšana 7 dienu laikā
          </div>
          {basketQuantity > 0 ? (
            <Link className="text-center text-sm font-black uppercase" to={getProductPath(product)}>
              {basketQuantity} grozā
            </Link>
          ) : null}
          <div className="flex-none flex gap-2 text-sm h-12">
            {basketQuantity > 0 ? (
              <>
                <button
                  className="flex-1 uppercase bg-red-500 hover:bg-red-600 cursor-pointer text-white shadow px-2"
                  type="button"
                  aria-label={`Noņemt ${product.name} no groza`}
                  onClick={() => onRemoveFromBasket(product.id)}
                >{"Noņemt"}</button>
                <Link
                  className="flex-3 inline-flex items-center justify-center border border-[var(--border)] px-2 text-center font-black uppercase text-[var(--text)]"
                  to="/basket"
                >{"Uz grozu"}</Link>
                <button
                  className="flex-1 uppercase bg-red-500 hover:bg-red-600 cursor-pointer text-white shadow px-2"
                  type="button"
                  aria-label={`Pievienot vēl vienu ${product.name} grozam`}
                  onClick={() => onAddToBasket(product.id)}
                >{"+1"}</button>
              </>
            ) : (
              <button
                className="flex-1 uppercase text-sm bg-red-500 hover:bg-red-600 cursor-pointer text-white shadow py-2"
                type="button"
                aria-label={`Pievienot ${product.name} grozam`}
                onClick={() => onAddToBasket(product.id)}
              >{"Pievienot"}</button>
            )}
          </div>
          <ProductCardPrice product={product} />
          <ProductBasketButtons basketQuantity={basketQuantity} product={product} onAddToBasket={onAddToBasket} onRemoveFromBasket={onRemoveFromBasket} />
        </section>
      </div>
    </main>
  );
}

function BuilderBanner({
  className = ""
}: {
  className?: string
}) {
  return (
    <section className={`${className} bg-cover bg-no-repeat py-8`}
    style={{
      backgroundImage: `linear-gradient(90deg, #010000 0 30%, transparent 50%), url(${footerHeroUrl})`,
      backgroundPosition: "center, right center",
    }}
    >
      <div className={`${containerClass}`}>
        <div className="max-w-lg">
          <h2 className="text-4xl uppercase text-balance mb-2">
            Izveido savu <span className="text-[#e02128]">ideālo sistēmu</span>
          </h2>
          <p className="text-white/80 mb-2 text-balance">{"Vai jums ir kādi jautājumi? Mūsu profesionālais atbalsts ir pieejams jums katru darba dienu pa e-pastu un tālruni."}</p>
          <Link
            className="inline-flex items-center justify-center rounded border border-white/20 px-4 font-black uppercase tracking-[0.04em] text-white min-h-10"
            to="/contact"
          >{"Atvērt saziņu formu"}</Link>
        </div>
      </div>
    </section>
  );
}

function ServiceItem({ service }: { service: Service }) {
  return (
    <article className="col-span-3 grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-[0.8rem] max-[58rem]:col-span-6">
      <span
        className="grid h-8 w-8 place-items-center rounded border border-white/40 text-[0.65rem] font-black uppercase text-[#a6adb5]"
        aria-hidden="true"
      >
        {service.icon}
      </span>
      <div>
        <h3 className="m-0 text-[0.78rem] font-black uppercase tracking-[0.02em] text-white/60">
          {service.title}
        </h3>
        <p className="mt-[0.28rem] mb-0 text-[0.8rem] leading-[1.45] text-[#a6adb5]">
          {service.text}
        </p>
      </div>
    </article>
  );
}

function Footer({
  className = "",
}: {
  className?: string,
}) {
  return (
    <>
      <footer className="bg-[#020304] text-white border-t border-white/10">
        <div className={`${containerClass} grid grid-cols-12 gap-x-[var(--gap)] gap-y-10 py-12 max-[58rem]:py-9`}>
          <section className="col-span-3 max-[58rem]:col-span-12" aria-label="Uzņēmuma dati">
            <img src={logoUrl} className="w-full max-w-24 mb-8" />
            <div className="mt-5 space-y-2 text-[0.92rem] leading-6 text-[#a6adb5]">
              <p className="m-0 font-bold text-white">SIA “Haas14”</p>
              <p className="m-0">Reģ. Nr. 50203543161</p>
              <p className="m-0">Hanzas iela 14, Rīga, LV 1010</p>
              <p className="m-0">
                <a className="text-[#f7f8f8] underline decoration-white/25 underline-offset-4 hover:text-[#e02128]" href="tel:+37126386666">
                  +371 26386666
                </a>
              </p>
              <p className="m-0">
                <a className="text-[#f7f8f8] underline decoration-white/25 underline-offset-4 hover:text-[#e02128]" href="mailto:info@scaudio.lv">
                  info@scaudio.lv
                </a>
              </p>
            </div>
          </section>
          <nav className="col-span-3 max-[58rem]:col-span-6 max-[36rem]:col-span-12" aria-label="Kategorijas">
            <h2 className="m-0 text-[0.78rem] font-black uppercase tracking-[0.08em] text-white/55">
              Kategorijas
            </h2>
            <ul className="mt-5 grid gap-3 p-0 text-[0.92rem] font-bold text-white [list-style:none]">
              {categories.map((category) => (
                <li key={category.title}>
                  <Link className="text-[#f7f8f8] hover:text-[#e02128]" to={getCategoryPath(category)}>
                    {category.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <nav className="col-span-3 max-[58rem]:col-span-6 max-[36rem]:col-span-12" aria-label="Zīmoli">
            <h2 className="m-0 text-[0.78rem] font-black uppercase tracking-[0.08em] text-white/55">
              Zīmoli
            </h2>
            <ul className="mt-5 grid gap-3 p-0 text-[0.92rem] font-bold text-white [list-style:none]">
              {brands.map((brand) => (
                <li key={brand.name}>
                  <Link className="text-[#f7f8f8] hover:text-[#e02128]" to={getBrandPath(brand)}>
                    {brand.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <nav className="col-span-3 max-[58rem]:col-span-6 max-[36rem]:col-span-12" aria-label="Par mums">
            <h2 className="m-0 text-[0.78rem] font-black uppercase tracking-[0.08em] text-white/55">
              Par mums
            </h2>
            <ul className="mt-5 grid gap-3 p-0 text-[0.92rem] font-bold text-white [list-style:none]">
              <li>
                <Link className="text-[#f7f8f8] hover:text-[#e02128]" to="/about">
                  Par mums
                </Link>
              </li>
              <li>
                <Link className="text-[#f7f8f8] hover:text-[#e02128]" to="/contact">
                  Sazināties
                </Link>
              </li>
              <li>
                <Link className="text-[#f7f8f8] hover:text-[#e02128]" to="/privacy-policy">
                  Privātuma politika
                </Link>
              </li>
              <li>
                <Link className="text-[#f7f8f8] hover:text-[#e02128]" to="/terms-of-sale">
                  Pārdošanas un piegādes noteikumi
                </Link>
              </li>
              <li>
                <Link className="text-[#f7f8f8] hover:text-[#e02128]" to="/return-policy">
                  Atgriešanas politika
                </Link>
              </li>
              <li>
                <Link className="text-[#f7f8f8] hover:text-[#e02128]" to="/warranty-policy">
                  Garantijas noteikumi
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </footer>
    </>
  );
}

function HomePage({
  basketItems,
  onAddToBasket,
  onRemoveFromBasket,
}: {
  basketItems: BasketItem[];
  onAddToBasket: (productId: number) => void;
  onRemoveFromBasket: (productId: number) => void;
}) {
  return (
    <main>
      <Hero />
      <HeroSubdivided />
      <Features />
      <SectionHead title="Iepirkties pēc kategorijas" action="Skatīt visus" to="/category" className="setThemeDark my-4"  />
      <CategorySection />
      <SectionHead title="Iepirkties pēc zīmola" action="Skatīt visus" to="/brand" className="setThemeDark my-4"   />
      <BrandRail />
      <SectionHead title="Akcijas Preces" action="Skatīt visus" to="/category/sale" className="setThemeLight pt-10" />
      <ProductSection basketItems={basketItems} className="setThemeLight" onAddToBasket={onAddToBasket} onRemoveFromBasket={onRemoveFromBasket} />
      <BuilderBanner />
    </main>
  );
}

function CompanyInfo({ compact = false }: { compact?: boolean }) {
  return (
    <section
      className={`col-span-full min-w-0 rounded-[0.35rem] border border-[var(--line)] bg-white/[0.03] p-[clamp(1rem,2.4vw,1.5rem)] ${
        compact ? "sm:col-span-7" : "sm:col-span-6"
      }`}
      aria-label="Uzņēmuma informācija"
    >
      <h2 className="m-0 text-[clamp(1.15rem,2vw,1.45rem)] font-black uppercase">{"Uzņēmuma informācija"}</h2>
      <div className="mt-4 grid gap-[0.85rem] text-base leading-[1.6] text-[#a6adb5]">
        <div>
          <div className="text-[1.2rem] font-black text-white">{"SIA “Haas14”"}</div>
          <div>{"Reģ. Nr. 50203543161"}</div>
          <div>{"Hanzas iela 14, Rīga, LV 1010"}</div>
        </div>
        <div>
          <a className="font-black text-white underline decoration-[#e02128]/70 underline-offset-[0.22rem]" href="tel:+37126386666">{"+371 26386666"}</a>
        </div>
        <div>
          <a className="font-black text-white underline decoration-[#e02128]/70 underline-offset-[0.22rem]" href="mailto:info@scaudio.lv">{"info@scaudio.lv"}</a>
        </div>
      </div>
    </section>
  );
}

function AboutPage() {
  return (
    <main className="min-h-[42rem] border-t border-[var(--line)] py-[clamp(2rem,5vw,4rem)]">
      <div className={`${containerClass} ${grid12Class} items-start`}>
        <header className="col-span-full mb-4 grid max-w-[42rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
            Par mums
          </p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">SC Audio</h1>
          <p className="m-0 text-base leading-[1.6] text-[#a6adb5]">Auto audio aprīkojuma pārdevējs un piegādātājs Latvijā.</p>
        </header>
        <CompanyInfo compact />
      </div>
    </main>
  );
}

type ContactStatus = {
  kind: "idle" | "success" | "error";
  message: string;
};

function ContactPage() {
  const [status, setStatus] = useState<ContactStatus>({ kind: "idle", message: "" });
  const [isSending, setIsSending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const phone = String(formData.get("phone") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    const website = String(formData.get("website") ?? "");

    if (!name || !email || !message) {
      setStatus({ kind: "error", message: "Lūdzu ievadiet vārdu, e-pastu un ziņojumu." });
      return;
    }

    setIsSending(true);
    setStatus({ kind: "idle", message: "" });

    try {
      const response = await fetch("https://api.scaudio.lv/v1/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, message, name, phone, website }),
      });

      const result = await response.json().catch(() => null) as { error?: string } | null;

      if (!response.ok) {
        throw new Error(result?.error || "Ziņojumu neizdevās nosūtīt.");
      }

      event.currentTarget.reset();
      setStatus({ kind: "success", message: "Ziņojums nosūtīts. Sazināsimies ar jums tuvākajā laikā." });
    } catch (error) {
      setStatus({
        kind: "error",
        message: error instanceof Error ? error.message : "Ziņojumu neizdevās nosūtīt.",
      });
    } finally {
      setIsSending(false);
    }
  }

  return (
    <main className="min-h-[42rem] border-t border-[var(--line)] py-[clamp(2rem,5vw,4rem)]">
      <div className={`${containerClass} ${grid12Class} items-start`}>
        <header className="col-span-full mb-4 grid max-w-[42rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
            Sazināties
          </p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">Kontakti</h1>
          <p className="m-0 text-base leading-[1.6] text-[#a6adb5]">Nosūtiet ziņu vai sazinieties ar mums pa tālruni un e-pastu.</p>
        </header>

        <form
          className="col-span-full grid min-w-0 gap-4 rounded-[0.35rem] border border-[var(--line)] bg-white/[0.03] p-[clamp(1rem,2.4vw,1.5rem)] sm:col-span-6"
          onSubmit={handleSubmit}
        >
          <h2 className="m-0 text-[clamp(1.15rem,2vw,1.45rem)] font-black uppercase">Nosūtīt ziņu</h2>
          <div className="grid gap-[0.45rem]">
            <label className="text-[0.78rem] font-black uppercase tracking-[0.04em] text-white" htmlFor="contact-name">Vārds</label>
            <input
              className="min-h-[2.8rem] w-full rounded border border-white/15 bg-black/30 px-[0.85rem] text-[0.95rem] text-white focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
              id="contact-name"
              name="name"
              type="text"
              autoComplete="name"
              required
            />
          </div>
          <div className="grid gap-[0.45rem]">
            <label className="text-[0.78rem] font-black uppercase tracking-[0.04em] text-white" htmlFor="contact-email">E-pasts</label>
            <input
              className="min-h-[2.8rem] w-full rounded border border-white/15 bg-black/30 px-[0.85rem] text-[0.95rem] text-white focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
              id="contact-email"
              name="email"
              type="email"
              autoComplete="email"
              required
            />
          </div>
          <div className="grid gap-[0.45rem]">
            <label className="text-[0.78rem] font-black uppercase tracking-[0.04em] text-white" htmlFor="contact-phone">Tālrunis</label>
            <input
              className="min-h-[2.8rem] w-full rounded border border-white/15 bg-black/30 px-[0.85rem] text-[0.95rem] text-white focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
              id="contact-phone"
              name="phone"
              type="tel"
              autoComplete="tel"
            />
          </div>
          <div className="grid gap-[0.45rem]">
            <label className="text-[0.78rem] font-black uppercase tracking-[0.04em] text-white" htmlFor="contact-message">Ziņojums</label>
            <textarea
              className="min-h-44 w-full resize-y rounded border border-white/15 bg-black/30 p-[0.85rem] text-[0.95rem] leading-[1.55] text-white focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
              id="contact-message"
              name="message"
              required
            />
          </div>
          <div className="hidden" aria-hidden="true">
            <label htmlFor="contact-website">Website</label>
            <input id="contact-website" name="website" tabIndex={-1} autoComplete="off" />
          </div>
          <button
            className="inline-flex min-h-[2.9rem] items-center justify-center rounded-[0.2rem] bg-[#e02128] px-[1.6rem] text-[0.78rem] font-black uppercase tracking-[0.04em] text-white disabled:cursor-wait disabled:opacity-[0.62]"
            type="submit"
            disabled={isSending}
          >
            {isSending ? "Sūta..." : "Sūtīt"}
          </button>
          {status.message ? (
            <p
              className={`m-0 text-[0.9rem] leading-[1.5] ${
                status.kind === "success" ? "text-[#86efac]" : status.kind === "error" ? "text-[#fca5a5]" : "text-[#a6adb5]"
              }`}
            >
              {status.message}
            </p>
          ) : null}
        </form>

        <CompanyInfo />
      </div>
    </main>
  );
}

function BasketPage({
  basketItems,
  onClearBasket,
  onRemoveFromBasket,
  onUpdateBasketQuantity,
}: {
  basketItems: BasketItem[];
  onClearBasket: () => void;
  onRemoveFromBasket: (productId: number) => void;
  onUpdateBasketQuantity: (productId: number, quantity: number) => void;
}) {
  const navigate = useNavigate();
  const [checkoutDraft, setCheckoutDraft] = useState<CheckoutDraft>(readStoredCheckoutDraft);
  const [calculation, setCalculation] = useState<BasketCalculationResponse | null>(null);
  const [calculationError, setCalculationError] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const detailedItems = basketItems
    .map((item) => {
      const product = getProductById(item.productId);

      if (!product) {
        return null;
      }

      const itemPrice = parseProductPrice(getActiveProductPrice(product));
      const subtotal = itemPrice * item.quantity;

      return { ...item, itemPrice, product, subtotal };
    })
    .filter((item): item is BasketItem & { itemPrice: number; product: Product; subtotal: number } => Boolean(item));

  const total = detailedItems.reduce((sum, item) => sum + item.subtotal, 0);
  const quantity = detailedItems.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    writeStoredCheckoutDraft(checkoutDraft);
  }, [checkoutDraft]);

  useEffect(() => {
    if (basketItems.length === 0) {
      setCalculation(null);
      setCalculationError("");
      setIsCalculating(false);
      return;
    }

    const controller = new AbortController();
    const request = getCheckoutPayload(basketItems, checkoutDraft);
    const timeout = window.setTimeout(() => {
      setIsCalculating(true);
      fetch(`${apiBaseUrl}/v1/basket/calculate`, {
        body: JSON.stringify(request),
        headers: { "Content-Type": "application/json" },
        method: "POST",
        signal: controller.signal,
      })
        .then(async (response) => {
          const result = await response.json().catch(() => null) as BasketCalculationResponse | null;

          if (!response.ok || !result?.ok) {
            setCalculation(null);
            setCalculationError(result?.message || result?.error || "Kopsummu neizdevās aprēķināt.");
            return;
          }

          setCalculation(result);
          setCalculationError("");
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setCalculation(null);
            setCalculationError("Kopsummu neizdevās aprēķināt.");
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsCalculating(false);
          }
        });
    }, 350);

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [basketItems, checkoutDraft]);

  function updateCheckoutDraft(field: keyof CheckoutDraft, value: string) {
    setCheckoutDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value,
    }));
  }

  async function handleSubmitOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (basketItems.length === 0) {
      setSubmitError("Grozs ir tukšs.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const response = await fetch(`${apiBaseUrl}/v1/orders`, {
        body: JSON.stringify(getCheckoutPayload(basketItems, checkoutDraft)),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
      const result = await response.json().catch(() => null) as {
        checkoutUrl?: string | null;
        message?: string;
        error?: string;
        orderId?: string;
        paymentError?: { message?: string } | null;
      } | null;

      if (!response.ok || !result?.orderId) {
        throw new Error(result?.message || result?.error || "Pasūtījumu neizdevās izveidot.");
      }

      if (result.checkoutUrl) {
        onClearBasket();
        window.location.href = result.checkoutUrl;
        return;
      }

      setSubmitError(result.paymentError?.message || "Pasūtījums ir izveidots, bet maksājumu vēl nevar inicializēt.");
      navigate(`/my-order/${result.orderId}`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Pasūtījumu neizdevās izveidot.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const subtotalLabel = calculation?.totals?.subtotal ?? formatPrice(total);
  const discountLabel = calculation?.totals?.discount ?? formatPrice(0);
  const deliveryLabel = calculation?.totals?.delivery ?? (checkoutDraft.deliveryMethod === "pickup" ? formatPrice(0) : checkoutDraft.deliveryMethod === "courier" ? formatCents(700) : formatCents(300));
  const totalLabel = calculation?.totals?.total ?? formatPrice(total + (checkoutDraft.deliveryMethod === "pickup" ? 0 : checkoutDraft.deliveryMethod === "courier" ? 7 : 3));

  return (
    <main className="setThemeLight min-h-[42rem] border-t border-[var(--border)] bg-[var(--bg)] py-[clamp(2rem,5vw,4rem)] text-[var(--text)]">
      <div className={`${containerClass} ${grid12Class} items-start`}>
        <header className="col-span-full mb-4 grid max-w-[42rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
            Grozs
          </p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">Jūsu grozs</h1>
          <p className="m-0 text-base leading-[1.6] text-[#747b84]">Pārskatiet preces un mainiet daudzumu pirms pasūtījuma.</p>
        </header>

        {detailedItems.length === 0 ? (
          <section className="col-span-full rounded-[0.35rem] border border-[var(--border)] bg-white p-[clamp(1rem,2.4vw,1.5rem)] text-[#121212] shadow-[0_0.4rem_1.4rem_rgba(0,0,0,0.06)]">
            <h2 className="m-0 text-[clamp(1.15rem,2vw,1.45rem)] font-black uppercase">Grozs ir tukšs</h2>
            <p className="mt-3 mb-0 text-[#747b84]">Pievienojiet produktus no kataloga, lai tie parādītos šeit.</p>
            <Link
              className="mt-5 inline-flex min-h-10 items-center justify-center rounded bg-[#e02128] px-4 text-[0.78rem] font-black uppercase tracking-[0.04em] text-white"
              to="/category/speakers"
            >
              Apskatīt produktus
            </Link>
          </section>
        ) : (
          <>
            <section className="col-span-full grid gap-3 lg:col-span-8" aria-label="Grozā pievienotās preces">
            {
                (calculation?.subtotalCents || 0) < 500_00 ? (<div className="border p-4 rounded shadow bg-white">
                  <div>{`Piegāde ir bezmaksas pasūtījumiem virs 500 EUR.`}</div>
                  <div>{`Līdz bezmaksas piegādei: ${formatCents(500_00 - (calculation?.subtotalCents || 0))}`}</div>
                </div>):(null)
              }
              {detailedItems.map((item) => (
                <article
                  className="grid gap-4 rounded-[0.35rem] border border-[var(--border)] bg-white p-4 text-[#121212] shadow-[0_0.4rem_1.4rem_rgba(0,0,0,0.06)] sm:grid-cols-[6rem_minmax(0,1fr)_8rem_6rem_auto] sm:items-center"
                  key={item.product.id}
                >
                  <div className="grid aspect-square place-items-center rounded bg-white text-[#121212]">
                    <img src={noImageLightUrl} className="h-full w-full object-contain p-2" alt=""
                    />
                  </div>
                  <div className="min-w-0">
                    <h2 className="m-0 text-base font-black uppercase">
                      <Link className="hover:text-[#e02128]" to={getProductPath(item.product)}>{item.product.name}</Link>
                    </h2>
                    <p className="mt-1 mb-0 text-sm leading-[1.45] text-[#747b84]">{item.product.description}</p>
                    <p className="mt-2 mb-0 text-xs font-black uppercase tracking-[0.04em] text-[#747b84]">ID: {item.product.id}</p>
                  </div>
                  <div>
                    <div className="text-[0.7rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Cena</div>
                    {item.product.sale_price !== null ? (
                      <div className="mt-1 grid gap-1">
                        <div className="text-sm font-black text-[#747b84] line-through">{item.product.price}</div>
                        <div className="font-black text-[#e02128]">{formatPrice(item.itemPrice)}</div>
                      </div>
                    ) : (
                      <div className="mt-1 font-black text-[#e02128]">{formatPrice(item.itemPrice)}</div>
                    )}
                  </div>
                  <label className="grid gap-1">
                    <span className="text-[0.7rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Daudzums</span>
                    <input
                      className="min-h-10 w-full rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                      min="1"
                      onChange={(event) => onUpdateBasketQuantity(item.product.id, Number(event.currentTarget.value))}
                      type="number"
                      value={item.quantity}
                    />
                  </label>
                  <button
                    className="inline-flex min-h-10 items-center justify-center rounded border border-black/15 px-3 text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#121212] hover:border-[#e02128] hover:text-[#e02128]"
                    onClick={() => onRemoveFromBasket(item.product.id)}
                    type="button"
                  >
                    Dzēst
                  </button>
                  {item.quantity > 1 ? (
                    <div className="border-t border-black/10 pt-3 text-sm text-[#747b84] sm:col-span-full">
                      Starpsumma: <span className="font-black text-[#121212]">{formatPrice(item.subtotal)}</span>
                    </div>
                  ) : null}
                </article>
              ))}
            </section>

            <form
              className="col-span-full grid gap-4 rounded-[0.35rem] border border-[var(--border)] bg-white p-[clamp(1rem,2.4vw,1.5rem)] text-[#121212] shadow-[0_0.4rem_1.4rem_rgba(0,0,0,0.06)] lg:col-span-4 lg:sticky lg:top-4"
              aria-label="Pasūtījuma noformēšana"
              onSubmit={handleSubmitOrder}
            >
              <h2 className="m-0 text-[clamp(1.15rem,2vw,1.45rem)] font-black uppercase">Kopsavilkums</h2>

              <dl className="grid gap-3">
                <div className="flex items-center justify-between gap-4 border-b border-black/10 pb-3">
                  <dt className="text-[#747b84]">Preces</dt>
                  <dd className="m-0 font-black">{quantity}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-[#747b84]">Preces kopā</dt>
                  <dd className="m-0 font-black">{subtotalLabel}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-[#747b84]">Atlaide</dt>
                  <dd className="m-0 font-black">-{discountLabel}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-[#747b84]">Piegāde</dt>
                  <dd className="m-0 font-black">{deliveryLabel}</dd>
                </div>
                <div className="flex items-center justify-between gap-4 border-t border-black/10 pt-3">
                  <dt className="font-black">Kopā</dt>
                  <dd className="m-0 text-2xl font-black text-[#e02128]">{totalLabel}</dd>
                </div>
              </dl>

              <fieldset className="grid gap-3 border-0 p-0">
                <legend className="mb-1 text-[0.78rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Pircējs</legend>
                <label className="grid gap-1">
                  <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Vārds</span>
                  <input
                    className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                    autoComplete="name"
                    onChange={(event) => updateCheckoutDraft("customerName", event.currentTarget.value)}
                    required
                    value={checkoutDraft.customerName}
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">E-pasts</span>
                  <input
                    className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                    autoComplete="email"
                    onChange={(event) => updateCheckoutDraft("customerEmail", event.currentTarget.value)}
                    required
                    type="email"
                    value={checkoutDraft.customerEmail}
                  />
                </label>
                <label className="grid gap-1">
                  <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Tālrunis</span>
                  <input
                    className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                    autoComplete="tel"
                    onChange={(event) => updateCheckoutDraft("customerPhone", event.currentTarget.value)}
                    type="tel"
                    value={checkoutDraft.customerPhone}
                  />
                </label>
              </fieldset>

              <fieldset className="grid gap-3 border-0 p-0">
                <legend className="mb-1 text-[0.78rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Piegāde</legend>
                <select
                  className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                  onChange={(event) => updateCheckoutDraft("deliveryMethod", event.currentTarget.value as DeliveryMethod)}
                  value={checkoutDraft.deliveryMethod}
                >
                  <option value="parcel_locker">Pakomāts - 3,00 €</option>
                  <option value="courier">Kurjers - 7,00 €</option>
                  <option value="pickup">Saņemšana Hanzas ielā - 0,00 €</option>
                </select>

                {checkoutDraft.deliveryMethod !== "pickup" ? (
                  <>
                    <label className="grid gap-1">
                      <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">
                        {checkoutDraft.deliveryMethod === "parcel_locker" ? "Pakomāta nosaukums" : "Saņēmējs / uzņēmums"}
                      </span>
                      <input
                        className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                        onChange={(event) => updateCheckoutDraft("deliveryName", event.currentTarget.value)}
                        value={checkoutDraft.deliveryName}
                      />
                    </label>
                    <label className="grid gap-1">
                      <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Adrese</span>
                      <input
                        className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                        autoComplete="shipping address-line1"
                        onChange={(event) => updateCheckoutDraft("addressLine1", event.currentTarget.value)}
                        required
                        value={checkoutDraft.addressLine1}
                      />
                    </label>
                    <label className="grid gap-1">
                      <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Adrese 2</span>
                      <input
                        className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                        autoComplete="shipping address-line2"
                        onChange={(event) => updateCheckoutDraft("addressLine2", event.currentTarget.value)}
                        value={checkoutDraft.addressLine2}
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="grid gap-1">
                        <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Pilsēta</span>
                        <input
                          className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                          autoComplete="shipping address-level2"
                          onChange={(event) => updateCheckoutDraft("city", event.currentTarget.value)}
                          required
                          value={checkoutDraft.city}
                        />
                      </label>
                      <label className="grid gap-1">
                        <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Pasta indekss</span>
                        <input
                          className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                          autoComplete="shipping postal-code"
                          onChange={(event) => updateCheckoutDraft("postalCode", event.currentTarget.value)}
                          required
                          value={checkoutDraft.postalCode}
                        />
                      </label>
                    </div>
                  </>
                ) : (
                  <p className="m-0 rounded bg-[#f7f8f8] p-3 text-sm font-bold leading-[1.45] text-[#747b84]">
                    Pasūtījumu varēs saņemt Hanzas ielā 14, Rīgā.
                  </p>
                )}
              </fieldset>

              <label className="grid gap-1">
                <span className="text-[0.72rem] font-black uppercase tracking-[0.04em] text-[#747b84]">Atlaides kods</span>
                <input
                  className="min-h-10 rounded border border-black/15 bg-white px-3 text-[#121212] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#e02128]/70"
                  onChange={(event) => updateCheckoutDraft("discountCode", event.currentTarget.value)}
                  value={checkoutDraft.discountCode}
                />
              </label>

              {isCalculating ? (
                <p className="m-0 text-sm font-bold text-[#747b84]">Pārrēķina kopsummu...</p>
              ) : null}
              {calculationError ? (
                <p className="m-0 text-sm font-bold text-[#b91c1c]">{calculationError}</p>
              ) : null}
              {submitError ? (
                <p className="m-0 text-sm font-bold text-[#b91c1c]">{submitError}</p>
              ) : null}

              <button
                className="inline-flex min-h-12 items-center justify-center rounded-[0.2rem] bg-[#e02128] px-4 text-[0.78rem] font-black uppercase tracking-[0.04em] text-white disabled:cursor-wait disabled:opacity-[0.62]"
                disabled={isSubmitting || isCalculating}
                type="submit"
              >
                {isSubmitting ? "Veido pasūtījumu..." : "Turpināt uz maksājumu"}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}

function OrderSummary({ order }: { order: PublicOrder }) {
  return (
    <section className="col-span-full grid gap-4 rounded-[0.35rem] border border-[var(--border)] bg-white p-[clamp(1rem,2.4vw,1.5rem)] text-[#121212] shadow-[0_0.4rem_1.4rem_rgba(0,0,0,0.06)] lg:col-span-8">
      <div className="grid gap-1">
        <h2 className="m-0 text-[clamp(1.15rem,2vw,1.45rem)] font-black uppercase">Pasūtījums {order.id}</h2>
        <p className="m-0 text-sm font-bold text-[#747b84]">Maksājuma statuss: {order.paymentStatus}</p>
      </div>
      <div className="grid gap-3">
        {order.items.map((item) => (
          <article className="grid gap-3 rounded border border-black/10 p-3 sm:grid-cols-[minmax(0,1fr)_5rem_7rem] sm:items-center" key={item.productId}>
            <div>
              <h3 className="m-0 text-base font-black uppercase">{item.name}</h3>
              <p className="mt-1 mb-0 text-sm text-[#747b84]">{item.description}</p>
            </div>
            <div className="text-sm font-black">{item.quantity} gab.</div>
            <div className="font-black text-[#e02128]">{formatCents(item.subtotalCents)}</div>
          </article>
        ))}
      </div>
    </section>
  );
}

function OrderTotals({ order, onPay, isPaying, paymentError }: { order: PublicOrder; onPay: () => void; isPaying: boolean; paymentError: string }) {
  return (
    <aside className="col-span-full grid gap-4 rounded-[0.35rem] border border-[var(--border)] bg-white p-[clamp(1rem,2.4vw,1.5rem)] text-[#121212] shadow-[0_0.4rem_1.4rem_rgba(0,0,0,0.06)] lg:col-span-4" aria-label="Pasūtījuma kopsavilkums">
      <h2 className="m-0 text-[clamp(1.15rem,2vw,1.45rem)] font-black uppercase">Kopsavilkums</h2>
      <dl className="grid gap-3">
        <div className="flex items-center justify-between gap-4">
          <dt className="text-[#747b84]">Preces kopā</dt>
          <dd className="m-0 font-black">{order.totals.subtotal}</dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-[#747b84]">Atlaide</dt>
          <dd className="m-0 font-black">-{order.totals.discount}</dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-[#747b84]">Piegāde</dt>
          <dd className="m-0 font-black">{order.totals.delivery}</dd>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-black/10 pt-3">
          <dt className="font-black">Kopā</dt>
          <dd className="m-0 text-2xl font-black text-[#e02128]">{order.totals.total}</dd>
        </div>
      </dl>
      <div className="rounded bg-[#f7f8f8] p-3 text-sm font-bold leading-[1.45] text-[#747b84]">
        <div>{order.customer.name}</div>
        <div>{order.customer.email}</div>
        <div>{order.deliveryMethod === "pickup" ? "Saņemšana Hanzas ielā 14, Rīgā" : `${order.address.line1}, ${order.address.city}`}</div>
      </div>
      {order.paymentStatus === "paid" ? (
        <p className="m-0 text-sm font-black uppercase tracking-[0.04em] text-[#15803d]">Pasūtījums ir apmaksāts.</p>
      ) : (
        <button
          className="inline-flex min-h-12 items-center justify-center rounded-[0.2rem] bg-[#e02128] px-4 text-[0.78rem] font-black uppercase tracking-[0.04em] text-white disabled:cursor-wait disabled:opacity-[0.62]"
          disabled={isPaying}
          onClick={onPay}
          type="button"
        >
          {isPaying ? "Atver maksājumu..." : "Apmaksāt pasūtījumu"}
        </button>
      )}
      {paymentError ? <p className="m-0 text-sm font-bold text-[#b91c1c]">{paymentError}</p> : null}
    </aside>
  );
}

function MyOrderPage({ orderId }: { orderId: string }) {
  const [order, setOrder] = useState<PublicOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState("");
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    let isCurrent = true;

    setIsLoading(true);
    setError("");
    fetch(`${apiBaseUrl}/v1/orders/${encodeURIComponent(orderId)}`)
      .then(async (response) => {
        const result = await response.json().catch(() => null) as { order?: PublicOrder; message?: string; error?: string } | null;

        if (!isCurrent) {
          return;
        }

        if (!response.ok || !result?.order) {
          setError(result?.message || result?.error || "Pasūtījums nav atrasts.");
          setOrder(null);
          return;
        }

        setOrder(result.order);
      })
      .catch(() => {
        if (isCurrent) {
          setError("Pasūtījumu neizdevās ielādēt.");
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [orderId]);

  async function handlePay() {
    if (!order) {
      return;
    }

    if (order.mollieCheckoutUrl && (order.paymentStatus === "open" || order.paymentStatus === "pending")) {
      window.location.href = order.mollieCheckoutUrl;
      return;
    }

    setIsPaying(true);
    setPaymentError("");

    try {
      const response = await fetch(`${apiBaseUrl}/v1/orders/${encodeURIComponent(order.id)}/payment`, { method: "POST" });
      const result = await response.json().catch(() => null) as { checkoutUrl?: string; order?: PublicOrder; message?: string; error?: string } | null;

      if (!response.ok || !result?.checkoutUrl) {
        throw new Error(result?.message || result?.error || "Maksājumu neizdevās inicializēt.");
      }

      if (result.order) {
        setOrder(result.order);
      }

      window.location.href = result.checkoutUrl;
    } catch (error) {
      setPaymentError(error instanceof Error ? error.message : "Maksājumu neizdevās inicializēt.");
    } finally {
      setIsPaying(false);
    }
  }

  return (
    <main className="setThemeLight min-h-[42rem] border-t border-[var(--border)] bg-[var(--bg)] py-[clamp(2rem,5vw,4rem)] text-[var(--text)]">
      <div className={`${containerClass} ${grid12Class} items-start`}>
        <header className="col-span-full mb-4 grid max-w-[42rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
            Pasūtījums
          </p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">Mans pasūtījums</h1>
          <p className="m-0 text-base leading-[1.6] text-[#747b84]">Pārbaudiet pasūtījuma un maksājuma statusu.</p>
        </header>

        {isLoading ? (
          <section className="col-span-full rounded-[0.35rem] border border-[var(--border)] bg-white p-[clamp(1rem,2.4vw,1.5rem)] text-[#121212]">Ielādē pasūtījumu...</section>
        ) : error ? (
          <section className="col-span-full rounded-[0.35rem] border border-[var(--border)] bg-white p-[clamp(1rem,2.4vw,1.5rem)] text-[#121212]">
            <h2 className="m-0 text-[clamp(1.15rem,2vw,1.45rem)] font-black uppercase">Pasūtījums nav atrasts</h2>
            <p className="mt-3 mb-0 text-[#747b84]">{error}</p>
          </section>
        ) : order ? (
          <>
            <OrderSummary order={order} />
            <OrderTotals order={order} onPay={handlePay} isPaying={isPaying} paymentError={paymentError} />
          </>
        ) : null}
      </div>
    </main>
  );
}

function MyOrderRoute() {
  const { orderId } = useParams();
  return <MyOrderPage orderId={orderId ?? ""} />;
}

function AdminOrderRow({
  adminToken,
  onOrderUpdated,
  order,
}: {
  adminToken: string;
  onOrderUpdated: (order: AdminOrder) => void;
  order: AdminOrder;
}) {
  const initialStatus = adminOrderStatuses.includes(order.status as AdminOrderStatus)
    ? order.status as AdminOrderStatus
    : "assembling";
  const [status, setStatus] = useState<AdminOrderStatus>(initialStatus);
  const [trackingCode, setTrackingCode] = useState(order.trackingCode ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSaving(true);
    setError("");

    try {
      const response = await fetch(`${apiBaseUrl}/v1/admin/order/${encodeURIComponent(order.id)}`, {
        body: JSON.stringify({ status, trackingCode }),
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
        method: "POST",
      });
      const result = await response.json().catch(() => null) as { order?: AdminOrder; message?: string; error?: string } | null;

      if (!response.ok || !result?.order) {
        throw new Error(result?.message || result?.error || "Pasūtījumu neizdevās atjaunināt.");
      }

      onOrderUpdated(result.order);
      setTrackingCode(result.order.trackingCode ?? "");
    } catch (error) {
      setError(error instanceof Error ? error.message : "Pasūtījumu neizdevās atjaunināt.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <tr className="align-top odd:bg-[#f7f8f8]">
      <td className="border-b border-black/10 p-3 font-black">{order.id}</td>
      <td className="border-b border-black/10 p-3 text-sm">
        <div className="font-black">{formatAdminDate(order.createdAt)}</div>
        <div className="mt-1 text-[#747b84]">Apmaksa: {formatAdminDate(order.paidAt)}</div>
        <div className="mt-1 text-[#747b84]">Nosūtīts: {formatAdminDate(order.dispatchedAt)}</div>
      </td>
      <td className="border-b border-black/10 p-3 text-sm">
        <div className="font-black">{order.customer.name}</div>
        <div>{order.customer.email}</div>
        <div>{order.customer.phone || "-"}</div>
      </td>
      <td className="border-b border-black/10 p-3 text-sm">
        <div className="font-black">{order.deliveryMethod}</div>
        <div>{order.deliveryMethod === "pickup" ? "Hanzas iela 14, Rīga" : `${order.address.line1}, ${order.address.city}`}</div>
        <div className="mt-1 text-[#747b84]">{order.trackingCode || "-"}</div>
      </td>
      <td className="border-b border-black/10 p-3 text-sm">
        {order.items.map((item) => (
          <div className="mb-2 last:mb-0" key={`${order.id}-${item.product_id}`}>
            <div className="font-black">{item.quantity} x {item.product_name}</div>
            <div className="text-[#747b84]">{formatPrice(item.subtotal_cents / 100)}</div>
          </div>
        ))}
      </td>
      <td className="border-b border-black/10 p-3 text-sm">
        <div className="font-black">{order.totals.total}</div>
        <div className="text-[#747b84]">Preces: {order.totals.subtotal}</div>
        <div className="text-[#747b84]">Atlaide: {order.totals.discount}</div>
        <div className="text-[#747b84]">Piegāde: {order.totals.delivery}</div>
      </td>
      <td className="border-b border-black/10 p-3 text-sm">
        <div>Pasūtījums: <span className="font-black">{order.status}</span></div>
        <div>Maksājums: <span className="font-black">{order.paymentStatus}</span></div>
        <div>Atmaksa: <span className="font-black">{order.refundStatus}</span></div>
        <div className="mt-2 break-all text-[#747b84]">Mollie: {String(order.raw.mollie_payment_id ?? "-")}</div>
      </td>
      <td className="border-b border-black/10 p-3 text-sm">
        <form className="grid min-w-[13rem] gap-2" onSubmit={handleSubmit}>
          <select
            className="min-h-10 rounded border border-black/15 bg-white px-2 font-bold"
            value={status}
            onChange={(event) => setStatus(event.currentTarget.value as AdminOrderStatus)}
          >
            {adminOrderStatuses.map((statusOption) => (
              <option key={statusOption} value={statusOption}>{adminOrderStatusLabels[statusOption]}</option>
            ))}
          </select>
          <input
            className="min-h-10 rounded border border-black/15 px-2"
            onChange={(event) => setTrackingCode(event.currentTarget.value)}
            placeholder="Izsekošanas kods"
            value={trackingCode}
          />
          <button
            className="inline-flex min-h-10 items-center justify-center rounded-[0.2rem] bg-[#121212] px-3 text-[0.75rem] font-black uppercase tracking-[0.04em] text-white disabled:cursor-wait disabled:opacity-[0.62]"
            disabled={isSaving}
            type="submit"
          >
            {isSaving ? "Saglabā..." : "Saglabāt"}
          </button>
          {error ? <p className="m-0 text-xs font-bold text-[#b91c1c]">{error}</p> : null}
        </form>
      </td>
      <td className="border-b border-black/10 p-3 text-sm">
        <details>
          <summary className="cursor-pointer font-black">Dati</summary>
          <pre className="mt-2 max-h-[24rem] max-w-[26rem] overflow-auto rounded bg-[#121212] p-3 text-xs leading-[1.45] text-white">{getJsonPreview(order)}</pre>
        </details>
        <div className="mt-2 text-xs text-[#747b84]">
          E-pasti: {order.emailSends.length}, notikumi: {order.statusEvents.length}, maksājumi: {order.paymentEvents.length}
        </div>
      </td>
    </tr>
  );
}

function AdminOrdersPage() {
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem(adminTokenStorageKey) ?? "");
  const [adminTokenDraft, setAdminTokenDraft] = useState(adminToken);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadOrders(token: string) {
    if (!token) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${apiBaseUrl}/v1/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json().catch(() => null) as { orders?: AdminOrder[]; message?: string; error?: string } | null;

      if (!response.ok || !result?.orders) {
        throw new Error(result?.message || result?.error || "Pasūtījumus neizdevās ielādēt.");
      }

      setOrders(result.orders);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Pasūtījumus neizdevās ielādēt.");
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void loadOrders(adminToken);
  }, [adminToken]);

  function handleTokenSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = adminTokenDraft.trim();

    if (!token) {
      localStorage.removeItem(adminTokenStorageKey);
      setAdminToken("");
      setOrders([]);
      return;
    }

    localStorage.setItem(adminTokenStorageKey, token);
    setAdminToken(token);
  }

  function updateOrder(updatedOrder: AdminOrder) {
    setOrders((currentOrders) => currentOrders.map((order) => order.id === updatedOrder.id ? updatedOrder : order));
  }

  return (
    <main className="setThemeLight min-h-[42rem] border-t border-[var(--border)] bg-[var(--bg)] py-[clamp(2rem,5vw,4rem)] text-[var(--text)]">
      <div className={containerClass}>
        <header className="mb-4 grid max-w-[52rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">Administrācija</p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">Pasūtījumi</h1>
        </header>

        <form className="mb-4 flex flex-wrap items-end gap-3 rounded-[0.35rem] border border-[var(--border)] bg-white p-4 text-[#121212]" onSubmit={handleTokenSubmit}>
          <label className="grid min-w-[18rem] flex-1 gap-2 text-sm font-black uppercase tracking-[0.04em]">
            Admin API token
            <input
              className="min-h-11 rounded border border-black/15 px-3 text-base normal-case tracking-normal"
              onChange={(event) => setAdminTokenDraft(event.currentTarget.value)}
              type="password"
              value={adminTokenDraft}
            />
          </label>
          <button className="inline-flex min-h-11 items-center justify-center rounded-[0.2rem] bg-[#e02128] px-4 text-[0.78rem] font-black uppercase tracking-[0.04em] text-white" type="submit">
            Ielādēt
          </button>
          {adminToken ? (
            <button
              className="inline-flex min-h-11 items-center justify-center rounded-[0.2rem] border border-black/15 px-4 text-[0.78rem] font-black uppercase tracking-[0.04em]"
              onClick={() => void loadOrders(adminToken)}
              type="button"
            >
              Atjaunot
            </button>
          ) : null}
        </form>

        {isLoading ? <section className="rounded-[0.35rem] border border-[var(--border)] bg-white p-4 text-[#121212]">Ielādē pasūtījumus...</section> : null}
        {error ? <section className="rounded-[0.35rem] border border-[var(--border)] bg-white p-4 font-bold text-[#b91c1c]">{error}</section> : null}

        {orders.length > 0 ? (
          <section className="overflow-x-auto rounded-[0.35rem] border border-[var(--border)] bg-white text-[#121212] shadow-[0_0.4rem_1.4rem_rgba(0,0,0,0.06)]">
            <table className="min-w-[92rem] w-full border-collapse text-left">
              <thead className="bg-[#121212] text-xs uppercase tracking-[0.04em] text-white">
                <tr>
                  <th className="p-3">Nr.</th>
                  <th className="p-3">Laiki</th>
                  <th className="p-3">Klients</th>
                  <th className="p-3">Piegāde</th>
                  <th className="p-3">Preces</th>
                  <th className="p-3">Summas</th>
                  <th className="p-3">Statusi</th>
                  <th className="p-3">Darbība</th>
                  <th className="p-3">Pilnie dati</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <AdminOrderRow adminToken={adminToken} key={order.id} onOrderUpdated={updateOrder} order={order} />
                ))}
              </tbody>
            </table>
          </section>
        ) : !isLoading && adminToken && !error ? (
          <section className="rounded-[0.35rem] border border-[var(--border)] bg-white p-4 text-[#121212]">Pasūtījumu nav.</section>
        ) : null}
      </div>
    </main>
  );
}

function PurchaseConfirmedPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order") ?? "";

  if (!orderId) {
    return (
      <main className="setThemeLight min-h-[42rem] border-t border-[var(--border)] bg-[var(--bg)] py-[clamp(2rem,5vw,4rem)] text-[var(--text)]">
        <div className={containerClass}>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">Pasūtījums nav norādīts</h1>
        </div>
      </main>
    );
  }

  return <MyOrderPage orderId={orderId} />;
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <main className="min-h-[42rem] border-t border-[var(--line)] py-[clamp(2rem,5vw,4rem)]">
      <div className={containerClass}>
        <header className="mb-4 grid max-w-[42rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
            {title}
          </p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">{title}</h1>
          <p className="m-0 text-base leading-[1.6] text-[#a6adb5]">TBA</p>
        </header>
      </div>
    </main>
  );
}

function PolicyHighlights({ items }: { items: PolicyHighlight[] }) {
  return (
    <section aria-label="Svarīgākie noteikumi" className={policyHighlightGridClass}>
      {items.map((item) => (
        <article className={policyHighlightCardClass} key={item.title}>
          <div className="flex gap-2">
            <img src={checkUrl} alt="" className="max-w-6 max-h-6 inline-block m-1 object-contain" />
            <h2 className={policyHighlightTitleClass}>{item.title}</h2>
          </div>
          <p className={policyHighlightTextClass}>{item.text}</p>
        </article>
      ))}
    </section>
  );
}

function PolicySummaryPage({
  intro,
  sections,
  title,
}: {
  intro: string;
  sections: PolicySummarySection[];
  title: string;
}) {
  return (
    <main className="min-h-[42rem] border-t border-[var(--line)] py-[clamp(2rem,5vw,4rem)]">
      <div className={containerClass}>
        <article className={policySummaryClass}>
          <header className={policySummaryHeaderClass}>
            <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
              Ātrais pārskats
            </p>
            <h1 className={policySummaryTitleClass}>{title}</h1>
            <p className={policySummaryIntroClass}>{intro}</p>
          </header>

          <div className={policySummaryGridClass}>
            {sections.map((section) => (
              <section className={policySummarySectionClass} key={section.title}>
                <div className="flex gap-2">
                  <img src={checkUrl} alt="" className="max-w-6 max-h-6 inline-block m-1 object-contain" />
                  <h2 className={policySummarySectionTitleClass}>{section.title}</h2>
                </div>
                <ul className={policySummaryListClass}>
                  {section.bullets.map((bullet) => (
                    <li className={policySummaryListItemClass} key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <Link className={policySummaryLinkClass} to="/terms-of-sale">
            Skatīt pilnos pārdošanas noteikumus
          </Link>
        </article>
      </div>
    </main>
  );
}

function MarkdownPage({ page }: { page: (typeof legalPages)[keyof typeof legalPages] }) {
  const highlights = page === legalPages.termsOfSale
    ? salesPolicyHighlights
    : page === legalPages.privacyPolicy
      ? privacyPolicyHighlights
      : null;

  return (
    <main className="min-h-[42rem] border-t border-[var(--line)] py-[clamp(2rem,5vw,4rem)]">
      <div className={containerClass}>
        {highlights ? <PolicyHighlights items={highlights} /> : null}
        <article
          aria-label={page.title}
          className="legal-content"
          dangerouslySetInnerHTML={{ __html: page.html }}
        />
      </div>
    </main>
  );
}

function NotFoundPage() {
  return (
    <main className="min-h-[42rem] border-t border-[var(--line)] py-[clamp(2rem,5vw,4rem)]">
      <div className={containerClass}>
        <header className="mb-4 grid max-w-[42rem] gap-3">
          <p className="m-0 mb-[1.1rem] text-[0.8rem] font-extrabold uppercase tracking-[0.04em] text-[#e02128]">
            404
          </p>
          <h1 className="m-0 text-[clamp(2.3rem,5vw,4.2rem)] leading-[0.95] uppercase">Lapa nav atrasta</h1>
          <p className="m-0 text-base leading-[1.6] text-[#a6adb5]">Šāda lapa neeksistē. Izmantojiet navigāciju, lai atgrieztos katalogā.</p>
        </header>
      </div>
    </main>
  );
}

function useAppOutletContext() {
  return useOutletContext<AppOutletContext>();
}

function HomeRoute() {
  const { basketItems, addToBasket, removeFromBasket } = useAppOutletContext();
  return <HomePage basketItems={basketItems} onAddToBasket={addToBasket} onRemoveFromBasket={removeFromBasket} />;
}

function ProductListRouteElement({ route }: { route: ProductListRoute }) {
  const { basketItems, addToBasket, removeFromBasket } = useAppOutletContext();
  return (
    <ProductListPage
      basketItems={basketItems}
      route={route}
      onAddToBasket={addToBasket}
      onRemoveFromBasket={removeFromBasket}
    />
  );
}

function BrandListRouteElement({ brand }: { brand: Brand }) {
  const { basketItems, addToBasket, removeFromBasket } = useAppOutletContext();
  return (
    <BrandListPage
      basketItems={basketItems}
      brand={brand}
      onAddToBasket={addToBasket}
      onRemoveFromBasket={removeFromBasket}
    />
  );
}

function ProductDetailRoute() {
  const { basketItems, addToBasket, removeFromBasket } = useAppOutletContext();
  return <ProductDetailPage basketItems={basketItems} onAddToBasket={addToBasket} onRemoveFromBasket={removeFromBasket} />;
}

function BasketRoute() {
  const { basketItems, clearBasket, removeFromBasket, updateBasketQuantity } = useAppOutletContext();
  return (
    <BasketPage
      basketItems={basketItems}
      onClearBasket={clearBasket}
      onRemoveFromBasket={removeFromBasket}
      onUpdateBasketQuantity={updateBasketQuantity}
    />
  );
}

function OpeningSoonPopup() {
  const [hasEntered, setHasEntered] = useState(false);
  const [isVisible, setIsVisible] = useState(() => {
    try {
      return localStorage.getItem("opening-soon-dismissed") !== "1";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const frame = requestAnimationFrame(() => setHasEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const dismiss = () => {
    try {
      localStorage.setItem("opening-soon-dismissed", "1");
    } catch {
      // The popup can still be dismissed for the current page view if storage is unavailable.
    }

    setIsVisible(false);
  };

  return (
    <div
      className={`fixed inset-0 z-[100] grid place-items-center bg-black/60 p-4 transition-opacity duration-[280ms] ease-out ${
        hasEntered ? "opacity-100" : "opacity-0"
      }`}
    >
      <section
        aria-label="Veikala atvēršanas paziņojums"
        aria-modal="true"
        className="relative grid aspect-[16/10] min-h-[min(72vh,30rem)] w-[min(calc(100vw_-_2rem),42rem)] place-items-center overflow-hidden rounded-[0.35rem] border border-white/20 bg-cover bg-center bg-no-repeat shadow-[0_1.5rem_4rem_rgba(0,0,0,0.58)]"
        role="dialog"
        style={{ backgroundImage: `url(${openingSoonUrl})` }}
      >
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.18),rgba(0,0,0,0.46))]"
        />
        <button
          aria-label="Aizvērt paziņojumu"
          className="absolute right-3 top-3 z-[2] grid aspect-square w-[2.4rem] place-items-center rounded-full border border-white/40 bg-black/45 text-[1.35rem] leading-none text-white hover:bg-[var(--red)] focus-visible:bg-[var(--red)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          onClick={dismiss}
          type="button"
        >
          x
        </button>
        <h2 className="relative z-[1] m-0 max-w-[min(calc(100%_-_3rem),34rem)] text-center text-[clamp(1.9rem,5vw,3.6rem)] font-black leading-[1.02] text-white [text-shadow:0_0.18rem_1rem_rgba(0,0,0,0.82)]">
          Veikals drīz tiks atvērts!
        </h2>
      </section>
    </div>
  );
}

function RoutedApp() {
  const [basketItems, setBasketItems] = useState<BasketItem[]>(readStoredBasket);
  const basketQuantity = basketItems.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    writeStoredBasket(basketItems);
  }, [basketItems]);

  function addToBasket(productId: number) {
    setBasketItems((currentItems) => {
      const existingItem = currentItems.find((item) => item.productId === productId);

      if (existingItem) {
        return currentItems.map((item) =>
          item.productId === productId ? { ...item, quantity: item.quantity + 1 } : item
        );
      }

      return [...currentItems, { productId, quantity: 1 }];
    });
  }

  function updateBasketQuantity(productId: number, quantity: number) {
    const normalizedQuantity = Math.floor(quantity);

    setBasketItems((currentItems) => {
      if (!Number.isFinite(normalizedQuantity)) {
        return currentItems;
      }

      const nextQuantity = Math.min(99, Math.max(1, normalizedQuantity));

      return currentItems.map((item) =>
        item.productId === productId ? { ...item, quantity: nextQuantity } : item
      );
    });
  }

  function removeFromBasket(productId: number) {
    setBasketItems((currentItems) => currentItems.filter((item) => item.productId !== productId));
  }

  function clearBasket() {
    setBasketItems([]);
  }

  return (
    <>
      <style>{styles}</style>
      <div className="page">
        <OpeningSoonPopup />
        <HeaderBanner />
        <HeaderLinks />
        <HeaderMain basketQuantity={basketQuantity} />
        <Outlet
          context={{
            basketItems,
            addToBasket,
            clearBasket,
            removeFromBasket,
            updateBasketQuantity,
          }}
        />
        <Footer />
        <ScrollRestoration />
      </div>
    </>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <RoutedApp />,
    children: [
      { index: true, element: <HomeRoute /> },
      { path: "about", element: <AboutPage /> },
      { path: "category", element: <CategoryRootPage /> },
      { path: "brand", element: <BrandRootPage /> },
      { path: "product/:productId", element: <ProductDetailRoute /> },
      { path: "basket", element: <BasketRoute /> },
      { path: "my-order/:orderId", element: <MyOrderRoute /> },
      { path: "purchase-confirmed", element: <PurchaseConfirmedPage /> },
      { path: "admin/orders", element: <AdminOrdersPage /> },
      { path: "contact", element: <ContactPage /> },
      { path: "privacy-policy", element: <MarkdownPage page={legalPages.privacyPolicy} /> },
      {
        path: "warranty-policy",
        element: (
          <PolicySummaryPage
            intro="Šeit ir īss un praktisks pārskats par garantijas un preces neatbilstības pieteikšanu. Pilnais juridiskais teksts ir pieejams pārdošanas noteikumos."
            sections={warrantyPolicySections}
            title="Garantijas noteikumi"
          />
        ),
      },
      {
        path: "return-policy",
        element: (
          <PolicySummaryPage
            intro="Šeit ir īss un praktisks pārskats par atgriešanu, atteikuma tiesībām un atmaksu. Pilnais juridiskais teksts ir pieejams pārdošanas noteikumos."
            sections={returnPolicySections}
            title="Atgriešanas politika"
          />
        ),
      },
      { path: "terms-of-sale", element: <MarkdownPage page={legalPages.termsOfSale} /> },
      { path: "delivery-terms", element: <MarkdownPage page={legalPages.termsOfSale} /> },
      ...productListRoutes.map((route) => ({
        path: route.path.replace(/^\//, ""),
        element: <ProductListRouteElement route={route} />,
      })),
      ...brands.map((brand) => ({
        path: getBrandPath(brand).replace(/^\//, ""),
        element: <BrandListRouteElement brand={brand} />,
      })),
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);

(import.meta.hot.data.root ??= createRoot(elem)).render(app);
